import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom'; // React Router Link component
import { api as axios } from '../utils/apiClient';
import Sidebar from './Sidebar';
import './Reports.css';
import jsPDF from 'jspdf';
import logoImage from '../assets/Gold (2).svg';
import vajraLogo from '../assets/Vajra single logo.png';

/**
 * Central reporting hub for WebMonitor.
 *
 * This replaces the old single "Weekly Health Report" view and instead
 * focuses on:
 *  - Monitoring all reportable data in one place (logs, plugin logs, backups)
 *  - Time based filters (last 3 months, custom date range, single day)
 *  - One-click export to CSV and browser-based PDF (Print to PDF)
 */
const Reports = () => {
  const [activeReport, setActiveReport] = useState('plugin-logs');

  // Shared time filtering state
  const [rangeType, setRangeType] = useState('last_90_days'); // last_30_days | last_90_days | custom_range | single_day
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [singleDate, setSingleDate] = useState('');

  // Plugin‑log specific state (global across all sites)
  const [pluginLogs, setPluginLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(true);
  const [logsError, setLogsError] = useState(null);

  // Backup log specific state
  const [backupLogs, setBackupLogs] = useState([]);
  const [backupLogsLoading, setBackupLogsLoading] = useState(false);
  const [backupLogsError, setBackupLogsError] = useState(null);

  // Monthly Maintenance Report state
  const [sites, setSites] = useState([]);
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [availableMonths, setAvailableMonths] = useState([]);
  const [maintenanceReport, setMaintenanceReport] = useState(null);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [reportError, setReportError] = useState(null);

  // Generate last 3 months list
  useEffect(() => {
    if (activeReport === 'maintenance-report') {
      const now = new Date();
      const months = [];
      
      // Generate last 3 months including current month
      // Start from current month (i = 0) and go back 2 more months (i = 1, i = 2)
      for (let i = 0; i < 3; i++) {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const monthName = date.toLocaleString('default', { month: 'long' });
        const year = date.getFullYear();
        const monthValue = `${year}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        months.push({
          value: monthValue,
          label: `${monthName} ${year}`,
          month: monthName,
          year: year,
          monthIndex: date.getMonth(),
          yearIndex: date.getFullYear()
        });
      }
      
      setAvailableMonths(months);
      // Set default to the most recent month (first in the list) only if not already set
      if (months.length > 0 && !selectedMonth) {
        setSelectedMonth(months[0].value);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeReport]);

  // Fetch sites for Monthly Maintenance Report
  useEffect(() => {
    if (activeReport === 'maintenance-report') {
      const fetchSites = async () => {
        try {
          const response = await axios.get('/api/sites');
          setSites(response.data || []);
        } catch (err) {
          console.error('[Reports] Failed to load sites:', err);
        }
      };
      fetchSites();
    }
  }, [activeReport]);

  // Fetch global plugin logs once – they can be filtered client‑side
  useEffect(() => {
    const fetchLogs = async () => {
      setLogsLoading(true);
      setLogsError(null);
      try {
        const response = await axios.get('/api/sites/logs/all?limit=2000');
        if (response.data?.success) {
          const allLogs = response.data.logs || [];
          console.log('[Reports] Loaded plugin logs:', {
            total: allLogs.length,
            updateAvailableCount: allLogs.filter(log => log.action === 'update_available' || (log.action || '').toLowerCase().includes('update_available')).length,
            updateSuccessCount: allLogs.filter(log => log.action === 'update_success' || (log.action || '').toLowerCase().includes('update_success')).length,
            actionBreakdown: allLogs.reduce((acc, log) => {
              const action = (log.action || 'unknown').toLowerCase();
              acc[action] = (acc[action] || 0) + 1;
              return acc;
            }, {})
          });
          setPluginLogs(allLogs);
        } else {
          setLogsError(response.data?.message || 'Failed to load global plugin logs.');
        }
      } catch (err) {
        console.error('[Reports] Failed to load global plugin logs:', err);
        setLogsError(err.response?.data?.message || err.message || 'Failed to load global plugin logs.');
      } finally {
        setLogsLoading(false);
      }
    };

    fetchLogs();
  }, []);

  const now = useMemo(() => new Date(), []);

  const currentRange = useMemo(() => {
    let start = null;
    let end = null;

    if (rangeType === 'last_30_days') {
      end = now;
      start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    } else if (rangeType === 'last_90_days') {
      end = now;
      start = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
    } else if (rangeType === 'custom_range') {
      if (fromDate) {
        start = new Date(fromDate);
      }
      if (toDate) {
        // Include the full "to" day
        end = new Date(toDate);
        end.setHours(23, 59, 59, 999);
      }
    } else if (rangeType === 'single_day' && singleDate) {
      start = new Date(singleDate);
      start.setHours(0, 0, 0, 0);
      end = new Date(singleDate);
      end.setHours(23, 59, 59, 999);
    }

    return { start, end };
  }, [rangeType, fromDate, toDate, singleDate, now]);

  const filteredPluginLogs = useMemo(() => {
    const { start, end } = currentRange;
    if (!start && !end) {
      return pluginLogs;
    }

    return pluginLogs.filter((log) => {
      if (!log.createdAt) return false;
      const created = new Date(log.createdAt);
      if (start && created < start) return false;
      if (end && created > end) return false;
      return true;
    });
  }, [pluginLogs, currentRange]);

  // Fetch backup logs when backups tab is active
  useEffect(() => {
    if (activeReport === 'backups') {
      const fetchBackupLogs = async () => {
        setBackupLogsLoading(true);
        setBackupLogsError(null);
        try {
          const { start, end } = currentRange;
          const params = new URLSearchParams();
          if (start) {
            params.append('fromDate', start.toISOString());
          }
          if (end) {
            params.append('toDate', end.toISOString());
          }

          const response = await axios.get(`/api/backup-request/logs?${params.toString()}`);
          if (response.data?.success) {
            setBackupLogs(response.data.logs || []);
          } else {
            setBackupLogsError(response.data?.message || 'Failed to load backup logs.');
          }
        } catch (err) {
          console.error('[Reports] Failed to load backup logs:', err);
          setBackupLogsError(err.response?.data?.message || err.message || 'Failed to load backup logs.');
        } finally {
          setBackupLogsLoading(false);
        }
      };

      fetchBackupLogs();
    }
  }, [activeReport, currentRange]);

  const pluginSummary = useMemo(() => {
    const total = filteredPluginLogs.length;
    const byAction = filteredPluginLogs.reduce(
      (acc, log) => {
        const action = (log.action || 'other').toLowerCase();
        if (action.includes('update')) acc.updates += 1;
        else if (action === 'activated') acc.activations += 1;
        else if (action === 'deactivated') acc.deactivations += 1;
        else acc.other += 1;
        return acc;
      },
      { total, updates: 0, activations: 0, deactivations: 0, other: 0 }
    );

    // Debug: Log summary breakdown including update_available logs
    const updateAvailableCount = filteredPluginLogs.filter(log => {
      const action = (log.action || '').toLowerCase();
      return action === 'update_available' || action.includes('update_available');
    }).length;
    
    if (updateAvailableCount > 0) {
      console.log('[Reports] Plugin summary includes update_available logs:', {
        totalUpdates: byAction.updates,
        updateAvailableCount,
        totalLogs: total
      });
    }

    return byAction;
  }, [filteredPluginLogs]);

  const handleExportCsv = () => {
    let header = [];
    let rows = [];
    let filename = '';

    if (activeReport === 'plugin-logs') {
      if (!filteredPluginLogs.length) return;

      header = [
        'Created At',
        'Website',
        'Plugin',
        'Action',
        'Old Version',
        'New Version',
        'Email',
        'Message',
      ];

      rows = filteredPluginLogs.map((log) => [
        log.createdAt ? new Date(log.createdAt).toISOString() : '',
        log.siteUrl || '',
        log.pluginName || log.pluginSlug || '',
        log.action || '',
        log.oldVersion || '',
        log.newVersion || '',
        log.email || '',
        (log.message || log.errorCode || '').replace(/"/g, '""'),
      ]);

      filename = 'webmonitor-plugin-logs.csv';
    } else if (activeReport === 'backups') {
      if (!backupLogs.length) return;

      header = [
        'Date & Time',
        'File Backup',
        'Database Backup',
        'Storage Type',
        'Drive Status',
        'Drive Error',
        'User',
        'Site Name',
        'Site URL',
      ];

      rows = backupLogs.map((log) => {
        const dateTime = log.dateTime ? new Date(log.dateTime).toISOString() : '';
        return [
          dateTime,
          log.fileBackupSize || '',
          log.databaseBackupSize || '',
          log.storageType === 'google-drive' ? 'Google Drive' : log.storageType === 'email' ? 'Email' : 'Local',
          log.driveStatus || '',
          (log.driveError || '').replace(/"/g, '""'),
          log.userEmail || '',
          log.siteName || '',
          log.siteUrl || '',
        ];
      });

      filename = 'webmonitor-backup-logs.csv';
    } else {
      // Security Activity - no CSV export available
      return;
    }

    const csv =
      header.join(',') +
      '\n' +
      rows
        .map((r) => r.map((v) => `"${String(v)}"`).join(','))
        .join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Download Maintenance Report as PDF
  const handleDownloadMaintenanceReportPdf = async () => {
    if (!maintenanceReport) {
      alert('Please generate a report first');
      return;
    }

    // Create PDF in landscape orientation
    const doc = new jsPDF('landscape', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    const lineHeight = 7;
    let yPosition = margin;
    const maxY = pageHeight - margin;

    // Helper function to add a new page if needed (always landscape)
    const checkNewPage = (requiredSpace) => {
      if (yPosition + requiredSpace > maxY) {
        doc.addPage('landscape', 'mm', 'a4');
        yPosition = margin;
        return true;
      }
      return false;
    };

    // Format date for PDF
    const formatDateForPdf = (date) => {
      if (!date) return 'N/A';
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    // Helper function to calculate responsive column widths that fill full page width
    const calculateColumnWidths = (baseWidths, availableWidth) => {
      const totalBaseWidth = baseWidths.reduce((sum, w) => sum + w, 0);
      const scaleFactor = availableWidth / totalBaseWidth;
      return baseWidths.map(w => w * scaleFactor);
    };

    // Helper to draw table with header
    const drawTable = (headers, colWidths, rows, title = '') => {
      // Calculate available width for table (full page width minus margins)
      const availableWidth = pageWidth - (margin * 2);
      
      // Scale column widths to fill full available width
      const scaledColWidths = calculateColumnWidths(colWidths, availableWidth);
      
      // Calculate estimated height more accurately
      const headerHeight = lineHeight * 1.5; // Increased header height for better appearance
      const rowHeight = lineHeight * 1.3; // Increased row height for larger fonts
      const estimatedHeight = headerHeight + (rows.length * rowHeight) + lineHeight * 1.5;
      checkNewPage(estimatedHeight);
      
      if (title) {
        doc.setFontSize(22);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(15, 55, 50); // Dark green
        doc.text(title, margin, yPosition);
        yPosition += lineHeight * 1.8; // Proper spacing after title
      }

      // Table header background - start at current yPosition, use full width
      doc.setFillColor(15, 55, 50); // Dark green
      const headerTop = yPosition;
      doc.rect(margin, headerTop, availableWidth, headerHeight, 'F');
      
      // Table headers - increased font size
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(255, 255, 255); // White text
      let xPosition = margin + 6;
      const headerY = headerTop + (headerHeight / 2) + 2.5; // Center vertically in header
      
      headers.forEach((header, index) => {
        const headerText = String(header).replace(/\n/g, ' '); // Normalize: replace line breaks with space
        const maxHeaderWidth = scaledColWidths[index] - 12; // Increased padding to ensure text fits
        // Check if header text fits in available width
        const textWidth = doc.getTextWidth(headerText);
        if (textWidth <= maxHeaderWidth) {
          // Text fits, use it as-is
          doc.text(headerText, xPosition, headerY);
        } else {
          // Text doesn't fit, try to wrap but use first line
          const headerLines = doc.splitTextToSize(headerText, maxHeaderWidth);
          // Use first line, but ensure we're not cutting off too much
          doc.text(headerLines[0] || '', xPosition, headerY);
        }
        xPosition += scaledColWidths[index];
      });
      
      // Move yPosition to after header
      yPosition = headerTop + headerHeight + lineHeight * 0.3;

      // Table rows - increased font size
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0); // Black text
      
      rows.forEach((row, rowIndex) => {
        checkNewPage(lineHeight * 3);
        
        // Calculate row height based on content with optimized spacing
        let maxCellLines = 1;
        row.forEach((cell, cellIndex) => {
          const cellText = String(cell || 'N/A');
          const maxWidth = scaledColWidths[cellIndex] - 10;
          const lines = doc.splitTextToSize(cellText, maxWidth);
          // Limit cell lines to prevent excessive row height (max 3 lines for non-Notes)
          const limitedLines = lines.length > 3 ? lines.slice(0, 3) : lines;
          maxCellLines = Math.max(maxCellLines, limitedLines.length);
        });
        
        const rowTop = yPosition;
        // Use appropriate line spacing for larger fonts
        const rowLineSpacing = 0.9;
        const minRowHeight = lineHeight * 1.2; // Increased minimum row height for larger fonts
        const calculatedHeight = maxCellLines * lineHeight * rowLineSpacing;
        const currentRowHeight = Math.max(minRowHeight, calculatedHeight);
        
        // Alternate row background
        if (rowIndex % 2 === 0) {
          doc.setFillColor(249, 250, 251); // Light grey
          doc.rect(margin, rowTop, availableWidth, currentRowHeight, 'F');
        }
        
        // Draw cell borders - more professional styling
        doc.setDrawColor(209, 213, 219);
        doc.setLineWidth(0.2);
        xPosition = margin;
        scaledColWidths.forEach((width, idx) => {
          if (idx > 0) {
            doc.line(xPosition, rowTop, xPosition, rowTop + currentRowHeight);
          }
          xPosition += width;
        });
        
        // Draw cell content with optimized spacing
        xPosition = margin + 6;
        const cellLineSpacing = 0.85; // Match row height calculation
        row.forEach((cell, cellIndex) => {
          const cellText = String(cell || 'N/A');
          const maxWidth = scaledColWidths[cellIndex] - 10;
          let lines = [];
          
          // Special handling for Date column (last column) - prevent word breaking
          const isDateColumn = cellIndex === headers.length - 1 && headers[cellIndex] === 'Date';
          if (isDateColumn && cellText !== 'N/A' && cellText !== '-') {
            // For dates, try to keep on single line - check if it fits
            const textWidth = doc.getTextWidth(cellText);
            if (textWidth <= maxWidth) {
              // Date fits on one line, use it as-is
              lines = [cellText];
            } else {
              // Date doesn't fit, try smaller font to fit on one line
              doc.setFontSize(11);
              const smallerTextWidth = doc.getTextWidth(cellText);
              if (smallerTextWidth <= maxWidth) {
                lines = [cellText];
              } else {
                // Still doesn't fit, try even smaller
                doc.setFontSize(10);
                const evenSmallerWidth = doc.getTextWidth(cellText);
                if (evenSmallerWidth <= maxWidth) {
                  lines = [cellText];
                } else {
                  // Last resort: wrap at spaces only (not mid-word)
                  const words = cellText.split(/\s+/);
                  lines = [];
                  let currentLine = '';
                  words.forEach(word => {
                    const testLine = currentLine ? `${currentLine} ${word}` : word;
                    const testWidth = doc.getTextWidth(testLine);
                    if (testWidth <= maxWidth) {
                      currentLine = testLine;
                    } else {
                      if (currentLine) lines.push(currentLine);
                      currentLine = word;
                    }
                  });
                  if (currentLine) lines.push(currentLine);
                }
              }
              doc.setFontSize(12); // Reset font size
            }
          } else {
            // Regular text wrapping for other columns
            lines = doc.splitTextToSize(cellText, maxWidth);
          }
          
          // Limit lines to match row height calculation (max 3 lines)
          const displayLines = lines.length > 3 ? lines.slice(0, 3) : lines;
          
          // Top-align text in cell
          const cellStartY = rowTop + 4;
          displayLines.forEach((line, lineIdx) => {
            if (line.trim()) {
              doc.text(line, xPosition, cellStartY + (lineIdx * lineHeight * cellLineSpacing));
            }
          });
          
          xPosition += scaledColWidths[cellIndex];
        });
        
        // Move to next row
        yPosition = rowTop + currentRowHeight + lineHeight * 0.1;
      });
      
      // Add spacing after table
      yPosition += lineHeight * 1.5; // Increased spacing after table for section separation
    };

    // Cover Page / Title - Full Page Design
    const coverPageHeight = pageHeight;
    
    // Dark teal/green background for entire page
    doc.setFillColor(15, 55, 50); // Dark green/teal
    doc.rect(0, 0, pageWidth, coverPageHeight, 'F');
    
    // Add subtle diagonal texture lines (optional - creates depth)
    doc.setDrawColor(20, 70, 65);
    doc.setLineWidth(0.1);
    for (let i = -50; i < pageWidth + 50; i += 20) {
      doc.line(i, -20, i + 100, coverPageHeight + 20);
    }
    
    // Gold color for logo and text (#d3af70)
    const goldColor = { r: 211, g: 175, b: 112 };
    doc.setTextColor(goldColor.r, goldColor.g, goldColor.b);
    doc.setFillColor(goldColor.r, goldColor.g, goldColor.b);
    doc.setDrawColor(goldColor.r, goldColor.g, goldColor.b);
    
    // === Logo from SVG file (PDF cover only) ===
    // Use the same logo image file as the Report page
    // Define the horizontal band where the logo and top line sit
    // Positioned at the very top of the page
    const bandLineY = margin; // At the top margin (15mm from top)
    
    // Logo dimensions - matching the screen appearance
    // The SVG has viewBox "0 0 476.9 69.699" but we'll scale it appropriately
    const logoHeight = 14.5; // mm - approximately matches 50px screen size
    const logoAspectRatio = 476.9 / 69.699; // SVG aspect ratio (width/height)
    const logoWidth = logoHeight * logoAspectRatio; // Calculate width to maintain aspect ratio
    
    // Position logo at top right
    const logoBlockRightX = pageWidth - margin; // right edge for logo block
    const logoStartX = logoBlockRightX - logoWidth;
    const logoTopY = bandLineY; // Align top of logo with band line
    const logoCenterY = logoTopY + (logoHeight / 2); // Center Y of the logo
    
    // Load and add the logo image to PDF
    // Convert SVG to canvas image that jsPDF can use
    try {
      // Create an image element to load the SVG
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      // Wait for image to load with timeout
      await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('Logo loading timeout'));
        }, 5000);
        
        img.onload = () => {
          clearTimeout(timeout);
          resolve();
        };
        img.onerror = (err) => {
          clearTimeout(timeout);
          console.error('Logo image error:', err);
          reject(err);
        };
        
        // Use the imported logo path
        img.src = logoImage;
      });
      
      // Create a canvas to render the SVG
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Set canvas size to match desired PDF size (convert mm to pixels at 96 DPI)
      const dpi = 96;
      const pixelsPerMm = dpi / 25.4;
      canvas.width = logoWidth * pixelsPerMm;
      canvas.height = logoHeight * pixelsPerMm;
      
      // Draw the image on canvas
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      
      // Convert canvas to data URL (PNG format)
      const logoDataUrl = canvas.toDataURL('image/png');
      
      // Add logo image to PDF
      doc.addImage(logoDataUrl, 'PNG', logoStartX, logoTopY, logoWidth, logoHeight);
      console.log('Logo added to PDF successfully');
    } catch (error) {
      console.error('Error loading logo:', error);
      console.error('Logo image path:', logoImage);
      // Fallback: continue without logo if loading fails
    }
    
    // Top line - extends from left margin up to just before the logo
    // Aligned vertically with the center of the logo
    const topLineY = logoCenterY; // Center the line with the logo
    const topLineEndX = logoStartX - 10; // Stop a little before the logo block
    doc.setDrawColor(211, 175, 112); // #d3af70
    doc.setLineWidth(1.0); // Increased from 0.5 to 1.0 for better visibility
    doc.line(margin, topLineY, topLineEndX, topLineY);
    
    // Title Section - Left aligned, moved down from center
    // Adding offset to move title section lower on the page
    const titleOffset = 30; // Offset in mm to move section down
    const titleStartY = pageHeight / 2 - lineHeight * 1.5 + titleOffset;
    
    // Title - Combined on same line
    const siteName = maintenanceReport.site.name || maintenanceReport.site.url || 'Website';
    doc.setFontSize(42);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(211, 175, 112); // #d3af70
    doc.text(`Website Maintenance / ${siteName}`, margin, titleStartY, { align: 'left' });
    
    // Line below title
    const titleLineY = titleStartY + lineHeight * 1.8;
    const titleLineWidth = 150;
    doc.line(margin, titleLineY, margin + titleLineWidth, titleLineY);
    
    // Subtitle
    const subtitleY = titleLineY + lineHeight * 2.2;
    doc.setFontSize(20);
    doc.setFont('helvetica', 'normal');
    doc.text(`Monthly Report - ${maintenanceReport.month} - ${maintenanceReport.year}`, margin, subtitleY, { align: 'left' });
    
    // Generated date at bottom left - moved down (closer to bottom)
    const generatedY = pageHeight - 10; // Moved down from pageHeight - 20 to pageHeight - 10
    
    // Bottom line removed - no longer needed
    doc.setFontSize(12);
    doc.text(`Generated: ${formatDateForPdf(maintenanceReport.generatedAt)}`, margin, generatedY, { align: 'left' });
    
    // Add new page for Overview section (separate from cover page)
    doc.addPage('landscape', 'mm', 'a4');
    yPosition = margin;
    doc.setTextColor(0, 0, 0); // Black text for white background
    
    // Overview Section on separate page
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(15, 55, 50); // Dark green
    doc.text('Overview', margin, yPosition);
    yPosition += lineHeight * 1.8; // Proper spacing after title to prevent overlap

    // Custom Overview table for white background
    const overviewHeaders = ['Work Plan / Task List', 'Status'];
    const overviewBaseWidths = [pageWidth - margin * 2 - 30, 30];
    const overviewAvailableWidth = pageWidth - (margin * 2);
    const overviewColWidths = calculateColumnWidths(overviewBaseWidths, overviewAvailableWidth);
    
    // Determine status for each section based on actual data
    const hasCoreUpdates = maintenanceReport.coreUpdates && (
      maintenanceReport.coreUpdates.wordpress || maintenanceReport.coreUpdates.php
    );
    const hasPluginUpdates = maintenanceReport.pluginUpdates && maintenanceReport.pluginUpdates.length > 0;
    const hasThemeUpdates = maintenanceReport.themeUpdates && maintenanceReport.themeUpdates.theme;
    const hasBackups = maintenanceReport.backups && (
      (maintenanceReport.backups.total > 0) || 
      (maintenanceReport.backups.backupList && maintenanceReport.backups.backupList.length > 0)
    );
    const hasSecurity = maintenanceReport.security && (
      maintenanceReport.security.malwareFound !== undefined || 
      maintenanceReport.security.blacklisted !== undefined ||
      maintenanceReport.security.riskLevel
    );
    
    // Helper function to draw a checkmark using lines (since jsPDF Helvetica doesn't support ✓)
    // x, y is the center point of the checkmark - perfectly centered both horizontally and vertically
    // Draws a classic V-shaped checkmark (not a diamond) - proper checkmark shape
    const drawCheckmark = (x, y, size = 2, color = [16, 163, 74]) => {
      doc.setLineWidth(1.8); // Optimal line width for clear checkmark
      doc.setDrawColor(color[0], color[1], color[2]); // Green color
      doc.setLineCap('round'); // Rounded line caps for smoother appearance
      doc.setLineJoin('round'); // Rounded line joins for smoother connection
      
      // Draw a proper checkmark: classic V shape (not diamond)
      // A checkmark is an open V shape - two lines meeting at a point
      // The shape should be clearly asymmetric to avoid diamond appearance
      
      // Calculate checkmark dimensions for proper V shape
      const checkWidth = size * 0.7;   // Width of checkmark
      const checkHeight = size * 0.55;  // Height of checkmark
      
      // Bottom-left starting point - creates the left arm of the checkmark
      // Positioned clearly to the left and below center
      const startX = x - (checkWidth * 0.5);
      const startY = y + (checkHeight * 0.4);
      
      // Center intersection point - where the two lines meet
      // Positioned to create a proper V shape (not symmetric to avoid diamond look)
      const midX = x - (checkWidth * 0.1);
      const midY = y + (checkHeight * 0.1);
      
      // Top-right ending point - creates the right arm of the checkmark
      // Positioned clearly to the right and above center
      const endX = x + (checkWidth * 0.5);
      const endY = y - (checkHeight * 0.5);
      
      // Draw checkmark as two connected lines forming a clear V shape
      // This creates a classic checkmark: open V shape, NOT a closed diamond
      // First line: from bottom-left to center (left arm)
      doc.line(startX, startY, midX, midY);
      // Second line: from center to top-right (right arm)
      doc.line(midX, midY, endX, endY);
    };
    
    const getStatusSymbol = (hasData) => hasData ? 'CHECKMARK' : 'N/A';
    
    const overviewRows = [
      ['Core update', getStatusSymbol(hasCoreUpdates)],
      ['Plugin Update (Staging & Live)', getStatusSymbol(hasPluginUpdates)],
      ['Theme Update', getStatusSymbol(hasThemeUpdates)],
      ['Backups (Weekly & Daily Backups)', getStatusSymbol(hasBackups)],
      ['Security Monitoring', getStatusSymbol(hasSecurity)]
    ];
    
    // Draw Overview table for white background
    const overviewHeaderHeight = lineHeight * 1.5; // Increased header height for better appearance
    const overviewHeaderTop = yPosition;
    
    // Table header with light green background - full width
    doc.setFillColor(240, 253, 244); // Light green background
    doc.rect(margin, overviewHeaderTop, overviewAvailableWidth, overviewHeaderHeight, 'F');
    
    // Add border to table header
    doc.setDrawColor(134, 239, 172); // Green border
    doc.setLineWidth(0.3);
    doc.rect(margin, overviewHeaderTop, overviewAvailableWidth, overviewHeaderHeight, 'S');
    
    // Draw vertical border between columns
    doc.setDrawColor(134, 239, 172);
    doc.setLineWidth(0.2);
    const statusColStartX = margin + overviewColWidths[0];
    doc.line(statusColStartX, overviewHeaderTop, statusColStartX, overviewHeaderTop + overviewHeaderHeight);
    
    // Table headers - consistent styling for both columns - increased font size
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(31, 41, 55); // Dark text
    const overviewHeaderY = overviewHeaderTop + (overviewHeaderHeight / 2) + 2.5; // Perfect vertical center
    
    // Work Plan / Task List header - left aligned with padding
    const workPlanHeaderX = margin + 6;
    doc.text(String(overviewHeaders[0]), workPlanHeaderX, overviewHeaderY);
    
    // Status header - perfectly centered within its column cell boundaries
    // Use exact same calculation as body cells for consistency
    const statusColStart = margin + overviewColWidths[0];
    const statusColEnd = statusColStart + overviewColWidths[1];
    // Perfect center: midpoint between cell start and end (same as body cells)
    const statusColCenterX = statusColStart + (overviewColWidths[1] / 2);
    const statusHeaderText = String(overviewHeaders[1]);
    const statusHeaderTextWidth = doc.getTextWidth(statusHeaderText);
    // Center text horizontally in the Status column cell
    doc.text(statusHeaderText, statusColCenterX - (statusHeaderTextWidth / 2), overviewHeaderY);
    
    // Move yPosition after header
    yPosition = overviewHeaderTop + overviewHeaderHeight + lineHeight * 0.2;
    
    // Table rows for white background - increased font size
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(13);
    doc.setTextColor(31, 41, 55); // Dark text for rows
    
      overviewRows.forEach((row, rowIndex) => {
      const rowTop = yPosition;
      const rowHeight = lineHeight * 1.4; // Increased row height for larger fonts
      
      // Alternate row background - full width
      if (rowIndex % 2 === 0) {
        doc.setFillColor(255, 255, 255); // White background
      } else {
        doc.setFillColor(249, 250, 251); // Light gray for alternating rows
      }
      doc.rect(margin, rowTop, overviewAvailableWidth, rowHeight, 'F');
      
      // Add border to each row - full width
      doc.setDrawColor(229, 231, 235); // Light gray border
      doc.setLineWidth(0.2);
      doc.rect(margin, rowTop, overviewAvailableWidth, rowHeight, 'S');
      
      // Draw vertical border between columns (same as header)
      doc.setDrawColor(229, 231, 235);
      doc.setLineWidth(0.2);
      const statusColStartX = margin + overviewColWidths[0];
      doc.line(statusColStartX, rowTop, statusColStartX, rowTop + rowHeight);
      
      // Calculate perfect center point for row (for Status column)
      const rowCenterY = rowTop + (rowHeight / 2);
      
      // Draw Work Plan / Task List column (left column)
      const taskListX = margin + 6;
      const taskListText = String(row[0] || 'N/A');
      const taskListMaxWidth = overviewColWidths[0] - 12;
      const taskListLines = doc.splitTextToSize(taskListText, taskListMaxWidth);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(13);
      doc.setTextColor(31, 41, 55);
      // Vertically center text in row
      const taskListY = rowCenterY - ((taskListLines.length - 1) * lineHeight * 0.4);
      taskListLines.forEach((line, lineIdx) => {
        doc.text(line, taskListX, taskListY + (lineIdx * lineHeight * 0.8));
      });
      
      // Draw Status column (right column) - perfectly centered
      // Calculate exact cell boundaries for Status column
      const statusColStart = margin + overviewColWidths[0];
      const statusColEnd = statusColStart + overviewColWidths[1];
      // Perfect center: midpoint between cell start and end
      const statusColCenterX = statusColStart + (overviewColWidths[1] / 2);
      const statusCellText = String(row[1] || 'N/A');
      
      if (statusCellText === 'CHECKMARK') {
        // Draw checkmark perfectly centered both horizontally and vertically
        // Use the exact center of the Status column cell
        drawCheckmark(statusColCenterX, rowCenterY, 3, [16, 163, 74]);
      } else {
        // Draw "N/A" text centered
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(13);
        doc.setTextColor(31, 41, 55);
        const naTextWidth = doc.getTextWidth(statusCellText);
        // Center text horizontally in the Status column cell
        doc.text(statusCellText, statusColCenterX - (naTextWidth / 2), rowCenterY);
      }
      
      // Move to next row
      yPosition = rowTop + rowHeight + lineHeight * 0.1;
    });
    
    // Add bottom border to table
    doc.setDrawColor(229, 231, 235); // Light gray border
    doc.setLineWidth(0.3);
    doc.line(margin, yPosition - 2, pageWidth - margin, yPosition - 2);
    
    yPosition += lineHeight * 0.5;
    
    // Add new page after Overview for other sections
    doc.addPage('landscape', 'mm', 'a4');
    yPosition = margin;
    doc.setTextColor(0, 0, 0); // Reset to black for rest of document

    // Core Updates Section
    checkNewPage(lineHeight * 8);
    yPosition += lineHeight * 1.0; // Add space before section title
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(15, 55, 50);
    doc.text('Core Updates', margin, yPosition);
    yPosition += lineHeight * 1.8; // Proper spacing after title to prevent overlap

    const coreHeaders = ['Category', 'Previous', 'Current', 'Latest', 'Notes', 'Comments', 'Date'];
    // Rebalanced: Increased Previous and Date column widths to fit text properly
    const coreColWidths = [25, 22, 20, 18, 20, 45, 22];
    const coreRows = [
      [
        'PHP Updates',
        maintenanceReport.coreUpdates.php.previousVersion,
        maintenanceReport.coreUpdates.php.currentVersion,
        maintenanceReport.coreUpdates.php.latestVersion,
        maintenanceReport.coreUpdates.php.notes,
        maintenanceReport.coreUpdates.php.comments,
        maintenanceReport.coreUpdates.php.date
      ],
      [
        'WordPress',
        maintenanceReport.coreUpdates.wordpress.previousVersion,
        maintenanceReport.coreUpdates.wordpress.currentVersion,
        maintenanceReport.coreUpdates.wordpress.latestVersion,
        maintenanceReport.coreUpdates.wordpress.notes,
        maintenanceReport.coreUpdates.wordpress.comments,
        maintenanceReport.coreUpdates.wordpress.date
      ],
      [
        'Theme Updates',
        maintenanceReport.coreUpdates.theme.previousVersion,
        maintenanceReport.coreUpdates.theme.currentVersion,
        maintenanceReport.coreUpdates.theme.latestVersion,
        maintenanceReport.coreUpdates.theme.notes,
        maintenanceReport.coreUpdates.theme.comments,
        maintenanceReport.coreUpdates.theme.date
      ]
    ];
    drawTable(coreHeaders, coreColWidths, coreRows);

    // Plugin Updates Section
    if (maintenanceReport.pluginUpdates.length > 0) {
      checkNewPage(lineHeight * 8);
      yPosition += lineHeight * 1.0; // Add space before section title
      doc.setFontSize(22);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(15, 55, 50);
      doc.text('Plugin Updates', margin, yPosition);
      yPosition += lineHeight * 1.8; // Proper spacing between title and table header to prevent overlap

      const pluginHeaders = ['Plugin Name', 'Old Version', 'New Version (Updated on)', 'Status', 'Notes'];
      // Rebalanced column widths: Notes gets more space, Version/Status stay compact
      // Plugin Name: 35, Old Version: 20, New Version: 25, Status: 18 (increased for "Inactive"), Notes: 62
      const pluginBaseWidths = [35, 20, 25, 18, 62];
      const pluginAvailableWidth = pageWidth - (margin * 2);
      const pluginColWidths = calculateColumnWidths(pluginBaseWidths, pluginAvailableWidth);
      
      // Table header background - single line header, use full width
      const headerHeight = lineHeight * 1.5; // Increased header height for better appearance
      const headerBoxTop = yPosition; // Start header box at current position
      doc.setFillColor(15, 55, 50); // Dark green
      doc.rect(margin, headerBoxTop, pluginAvailableWidth, headerHeight, 'F');
      
      // Table headers - all single line, normalized - increased font size
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(255, 255, 255); // White text
      let xPosition = margin + 6;
      const headerY = headerBoxTop + (headerHeight / 2) + 2.5; // Center vertically in header
      
      pluginHeaders.forEach((header, index) => {
        const headerText = String(header).replace(/\n/g, ' '); // Replace any line breaks with space
        const maxWidth = pluginColWidths[index] - 10;
        
        // Wrap header if needed, but prefer single line
        let headerLines = doc.splitTextToSize(headerText, maxWidth);
        // Use first line only for consistent header height
        const headerLineY = headerY;
        doc.text(headerLines[0] || '', xPosition, headerLineY);
        
        xPosition += pluginColWidths[index];
      });
      // Position yPosition after the header box
      yPosition = headerBoxTop + headerHeight + lineHeight * 0.3;

      // Table rows with multi-line support - increased font size
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0); // Black text
      
      maintenanceReport.pluginUpdates.forEach((plugin, rowIndex) => {
        // Check if we need a new page - account for multi-line rows
        checkNewPage(lineHeight * 5);
        
        const rowStartY = yPosition;
        
        // Alternate row background - will be drawn after we know the row height
        let maxLines = 1;
        const cellData = [];
        
        // Process each cell and calculate max lines needed
        xPosition = margin + 6;
        pluginHeaders.forEach((header, cellIndex) => {
          let cellText = '';
          if (cellIndex === 0) {
            cellText = plugin.name || 'Unknown';
          } else if (cellIndex === 1) {
            cellText = plugin.oldVersion || 'N/A';
          } else if (cellIndex === 2) {
            cellText = plugin.newVersion || 'N/A';
          } else if (cellIndex === 3) {
            cellText = plugin.status || 'N/A';
          } else if (cellIndex === 4) {
            // Format notes with better paragraph handling
            cellText = plugin.notes || 'No new update';
            // Clean up notes: remove extra whitespace, normalize line breaks
            cellText = String(cellText)
              .replace(/\r\n/g, '\n')
              .replace(/\r/g, '\n')
              .replace(/\n{3,}/g, '\n\n') // Max 2 consecutive newlines
              .trim();
          }
          
          // Prevent wrapping for Status column - keep "Active"/"Inactive" on one line
          let maxWidth = pluginColWidths[cellIndex] - 12; // Better padding for word wrapping
          let lines = [];
          
          if (cellIndex === 3) {
            // Status column: prevent word breaking - ALWAYS keep on single line
            // "Active" and "Inactive" should never wrap
            const statusText = String(cellText);
            // Set font to check width (use current font size)
            doc.setFontSize(12);
            const textWidth = doc.getTextWidth(statusText);
            // If text fits or is close, use single line
            if (textWidth <= maxWidth + 2) {
              lines = [statusText]; // Single line - no wrapping
            } else {
              // Even if slightly too wide, force single line (shouldn't happen with increased column width)
              lines = [statusText];
            }
          } else if (cellIndex === 4) {
            // Special handling for Notes column: wrap text efficiently
            // Split by newlines first, then wrap each paragraph with tighter spacing
            const paragraphs = String(cellText).split('\n');
            lines = [];
            paragraphs.forEach((para) => {
              const trimmedPara = para.trim();
              if (trimmedPara) {
                // Wrap each paragraph efficiently
                const wrappedLines = doc.splitTextToSize(trimmedPara, maxWidth);
                lines.push(...wrappedLines);
              }
            });
            // Limit Notes lines to prevent excessive row height (max 4 lines for compact display)
            if (lines.length > 4) {
              lines = lines.slice(0, 4);
              lines[3] = lines[3].substring(0, lines[3].length - 3) + '...';
            }
          } else {
            // Regular text wrapping for other columns (single line preferred)
            lines = doc.splitTextToSize(String(cellText), maxWidth);
            // Limit to 2 lines max for non-Notes columns
            if (lines.length > 2) {
              lines = lines.slice(0, 2);
            }
          }
          
          cellData.push({ text: cellText, lines: lines, x: xPosition, width: pluginColWidths[cellIndex] });
          maxLines = Math.max(maxLines, lines.length);
          xPosition += pluginColWidths[cellIndex];
        });
        
        // Calculate row height based on max lines with proper spacing to prevent overlap
        // Use adequate line spacing to prevent text overlap
        const lineSpacing = 1.0; // Increased spacing to prevent overlap
        const minRowHeight = lineHeight * 1.3; // Increased minimum row height for larger fonts
        const calculatedHeight = maxLines * lineHeight * lineSpacing;
        const rowHeight = Math.max(minRowHeight, calculatedHeight);
        
        // Draw row background if needed - at rowStartY position, use full width
        if (rowIndex % 2 === 0) {
          doc.setFillColor(249, 250, 251); // Light grey
          doc.rect(margin, rowStartY, pluginAvailableWidth, rowHeight, 'F');
        }
        
        // Draw cell borders for better separation - more professional styling
        doc.setDrawColor(209, 213, 219);
        doc.setLineWidth(0.2);
        let borderX = margin;
        pluginColWidths.forEach((width, idx) => {
          if (idx > 0) {
            doc.line(borderX, rowStartY, borderX, rowStartY + rowHeight);
          }
          borderX += width;
        });
        
        // Draw all cells with proper multi-line support
        // Top-align text within the row with adequate padding to prevent overlap
        const cellStartY = rowStartY + lineHeight * 1.1; // Increased padding from top
        
        // Draw each line of the row with consistent spacing from the top
        for (let lineIndex = 0; lineIndex < maxLines; lineIndex++) {
          xPosition = margin + 6;
          
          // Calculate Y position with consistent spacing to prevent overlap
          const currentLineY = cellStartY + (lineIndex * lineHeight * lineSpacing);
          
          cellData.forEach((cell, cellIndex) => {
            const line = cell.lines[lineIndex] || '';
            
            // Draw content for this cell (only if there's actual content)
            if (line.trim() || (cellIndex !== 4 && lineIndex === 0)) {
              // Color code status column
              if (cellIndex === 3) {
                if (line.toLowerCase().includes('active')) {
                  doc.setTextColor(22, 163, 74); // Green
                } else {
                  doc.setTextColor(217, 119, 6); // Orange
                }
              } else {
                doc.setTextColor(0, 0, 0); // Black
              }
              
              // For Notes column, use slightly smaller font for better fit
              if (cellIndex === 4) {
                if (line.trim()) {
                  doc.setFontSize(11);
                  doc.text(line, xPosition, currentLineY);
                  doc.setFontSize(12); // Reset font size
                }
                // No empty lines - text flows naturally
              } else {
                doc.text(line, xPosition, currentLineY);
              }
            }
            
            xPosition += cell.width;
          });
        }
        
        // Move to next row position - use rowStartY + rowHeight
        yPosition = rowStartY + rowHeight + lineHeight * 0.2; // Proper spacing between rows
      });
      
      yPosition += lineHeight * 1.5; // Increased spacing after table for section separation
    }

    // Theme Updates Section
    checkNewPage(lineHeight * 8);
    yPosition += lineHeight * 1.0; // Add space before section title
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(15, 55, 50);
    doc.text('Theme Updates', margin, yPosition);
    yPosition += lineHeight * 1.8; // Proper spacing after title to prevent overlap

    const themeHeaders = ['Theme', 'Old Version', 'New Version', 'Security', 'Status', 'Recommendations'];
    // Rebalanced: Version columns narrower, Recommendations wider
    const themeColWidths = [30, 18, 18, 25, 18, 40];
    const themeRows = [[
      maintenanceReport.themeUpdates.theme,
      maintenanceReport.themeUpdates.oldVersion,
      maintenanceReport.themeUpdates.newVersion,
      maintenanceReport.themeUpdates.security,
      maintenanceReport.themeUpdates.status,
      maintenanceReport.themeUpdates.recommendations
    ]];
    drawTable(themeHeaders, themeColWidths, themeRows);

    // Backup Section
    // Calculate space needed: title spacing + title height + spacing + table
    const backupHeaders = ['Summary', 'Details'];
    const backupColWidths = [pageWidth - margin * 2 - 50, 50];
    const backupRows = [
      ['Total Backups', maintenanceReport.backups.total.toString()],
      ['Total Restores', '0'], // Restore functionality removed
      ['Average Backup Size', maintenanceReport.backups.averageSize],
      ['Backup Frequency', maintenanceReport.backups.frequency]
    ];
    // Estimate table height: header + rows + spacing
    const estimatedTableHeight = (lineHeight * 1.5) + (backupRows.length * lineHeight * 1.3) + (lineHeight * 1.5);
    const titleSpace = lineHeight * 1.0 + lineHeight * 1.8; // Space before title + title + spacing
    const totalSpaceNeeded = titleSpace + estimatedTableHeight;
    checkNewPage(totalSpaceNeeded);
    
    yPosition += lineHeight * 1.0; // Add space before section title
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(15, 55, 50);
    doc.text('Backup - Daily/Weekly', margin, yPosition);
    yPosition += lineHeight * 1.8; // Proper spacing after title to prevent overlap

    drawTable(backupHeaders, backupColWidths, backupRows);

    // Individual Backup Details Table
    if (maintenanceReport.backups.backupList && maintenanceReport.backups.backupList.length > 0) {
      checkNewPage(lineHeight * 8);
      yPosition += lineHeight * 1.0; // Add space before subsection title
      doc.setFontSize(22);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(15, 55, 50);
      doc.text('Backup Details', margin, yPosition);
      yPosition += lineHeight * 1.8; // Proper spacing after title to prevent overlap

      const backupDetailHeaders = ['Date', 'Time', 'File Size', 'DB Size', 'Storage', 'Status'];
      const backupDetailColWidths = [35, 25, 25, 25, 30, 30];
      const backupDetailRows = maintenanceReport.backups.backupList.map(backup => [
        backup.date,
        backup.time,
        backup.fileBackupSize || 'N/A',
        backup.databaseBackupSize || 'N/A',
        backup.storageType === 'google-drive' ? 'Google Drive' : backup.storageType === 'email' ? 'Email' : 'Local',
        backup.driveStatus || 'N/A'
      ]);
      drawTable(backupDetailHeaders, backupDetailColWidths, backupDetailRows);
    }

    // Security Monitoring Section
    checkNewPage(lineHeight * 8);
    yPosition += lineHeight * 1.0; // Add space before section title
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(15, 55, 50);
    doc.text('Security Monitoring', margin, yPosition);
    yPosition += lineHeight * 1.8; // Proper spacing to prevent overlap

    // Security Status Cards
    const cardWidth = (pageWidth - margin * 2 - 10) / 2;
    const cardHeight = lineHeight * 3;
    
    // Determine security status from actual data
    const hasMalwareData = maintenanceReport.security && maintenanceReport.security.malwareFound !== undefined;
    const malwareStatus = hasMalwareData && maintenanceReport.security.malwareFound;
    const malwareText = malwareStatus ? 'No Malware Found' : (hasMalwareData ? 'Malware Detected' : 'N/A');
    const malwareDetail = malwareStatus ? 'Our scanner didn\'t detect any malware' : (hasMalwareData ? 'Malware was detected' : 'No scan data available');
    
    const hasBlacklistData = maintenanceReport.security && maintenanceReport.security.blacklisted !== undefined;
    const blacklistStatus = hasBlacklistData && !maintenanceReport.security.blacklisted;
    const blacklistText = blacklistStatus ? 'Site is not Blacklisted' : (hasBlacklistData ? 'Site is Blacklisted' : 'N/A');
    const blacklistCount = maintenanceReport.security?.blacklistCount || 0;
    const blacklistDetail = hasBlacklistData ? `${blacklistCount} Blacklists checked` : 'No scan data available';
    
    // Card 1: No Malware Found / Status
    doc.setFillColor(240, 253, 244); // Light green background
    doc.rect(margin, yPosition, cardWidth, cardHeight, 'F');
    doc.setDrawColor(134, 239, 172); // Green border
    doc.setLineWidth(1);
    doc.rect(margin, yPosition, cardWidth, cardHeight, 'S');
    
    // Show checkmark only if data exists and status is good
    if (hasMalwareData && malwareStatus) {
      // Draw green checkmark, centered vertically in card
      doc.setDrawColor(16, 163, 74); // Green color
      const checkX = margin + 10;
      const checkY = yPosition + (cardHeight / 2); // Center vertically
      // Use centered checkmark drawing
      const leftX = checkX - 2;
      const centerX = checkX;
      const rightX = checkX + 2;
      const topY = checkY - 1.5;
      const centerY = checkY + 1;
      const bottomY = checkY + 2.5;
      doc.setLineWidth(1.2);
      doc.line(leftX, centerY, centerX, bottomY);
      doc.line(centerX, bottomY, rightX, topY);
    } else if (hasMalwareData && !malwareStatus) {
      // Draw red X mark, centered vertically in card
      doc.setDrawColor(220, 38, 38); // Red color
      const xMarkX = margin + 10;
      const xMarkY = yPosition + (cardHeight / 2); // Center vertically
      const xSize = 2;
      doc.setLineWidth(1.2);
      // Draw X: two diagonal lines, centered
      doc.line(xMarkX - xSize, xMarkY - xSize, xMarkX + xSize, xMarkY + xSize);
      doc.line(xMarkX + xSize, xMarkY - xSize, xMarkX - xSize, xMarkY + xSize);
    }
    
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(31, 41, 55);
    doc.text(malwareText, margin + 20, yPosition + lineHeight * 0.8);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(12);
    doc.setTextColor(75, 85, 99);
    doc.text(malwareDetail, margin + 20, yPosition + lineHeight * 1.6);

    // Card 2: Site is not Blacklisted / Status
    doc.setFillColor(240, 253, 244);
    doc.rect(margin + cardWidth + 10, yPosition, cardWidth, cardHeight, 'F');
    doc.setDrawColor(134, 239, 172);
    doc.rect(margin + cardWidth + 10, yPosition, cardWidth, cardHeight, 'S');
    
    // Show checkmark only if data exists and status is good
    if (hasBlacklistData && blacklistStatus) {
      // Draw green checkmark, centered vertically in card
      doc.setDrawColor(16, 163, 74); // Green color
      const checkX = margin + cardWidth + 20;
      const checkY = yPosition + (cardHeight / 2); // Center vertically
      // Use centered checkmark drawing
      const leftX = checkX - 2;
      const centerX = checkX;
      const rightX = checkX + 2;
      const topY = checkY - 1.5;
      const centerY = checkY + 1;
      const bottomY = checkY + 2.5;
      doc.setLineWidth(1.2);
      doc.line(leftX, centerY, centerX, bottomY);
      doc.line(centerX, bottomY, rightX, topY);
    } else if (hasBlacklistData && !blacklistStatus) {
      // Draw red X mark, centered vertically in card
      doc.setDrawColor(220, 38, 38); // Red color
      const xMarkX = margin + cardWidth + 20;
      const xMarkY = yPosition + (cardHeight / 2); // Center vertically
      const xSize = 2;
      doc.setLineWidth(1.2);
      // Draw X: two diagonal lines, centered
      doc.line(xMarkX - xSize, xMarkY - xSize, xMarkX + xSize, xMarkY + xSize);
      doc.line(xMarkX + xSize, xMarkY - xSize, xMarkX - xSize, xMarkY + xSize);
    }
    
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(31, 41, 55);
    doc.text(blacklistText, margin + cardWidth + 30, yPosition + lineHeight * 0.8);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(12);
    doc.setTextColor(75, 85, 99);
    doc.text(blacklistDetail, margin + cardWidth + 30, yPosition + lineHeight * 1.6);
    
    yPosition += cardHeight + lineHeight * 1.5; // Increased spacing after security cards

    // Website Details Card
    const detailsCardHeight = lineHeight * 6;
    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(229, 231, 235);
    doc.rect(margin, yPosition, pageWidth - margin * 2, detailsCardHeight, 'F');
    doc.rect(margin, yPosition, pageWidth - margin * 2, detailsCardHeight, 'S');
    
    const detailsY = yPosition + lineHeight;
    
    // URL on the left
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(31, 41, 55);
    doc.text('URL:', margin + 5, detailsY);
    doc.setFont('helvetica', 'normal');
    doc.text(maintenanceReport.site.url, margin + 20, detailsY);
    
    // Details on the right
    const detailsX = margin + (pageWidth - margin * 2) * 0.55;
    let detailsStartY = detailsY;
    
    doc.setFontSize(13);
    doc.setTextColor(75, 85, 99);
    doc.setFont('helvetica', 'bold');
    doc.text('IP address:', detailsX, detailsStartY);
    doc.setFont('helvetica', 'normal');
    doc.text(maintenanceReport.security.ip || 'N/A', detailsX + 35, detailsStartY);
    detailsStartY += lineHeight;
    
    doc.setFont('helvetica', 'bold');
    doc.text('Hosting:', detailsX, detailsStartY);
    doc.setFont('helvetica', 'normal');
    doc.text(maintenanceReport.security.hosting, detailsX + 35, detailsStartY);
    detailsStartY += lineHeight;
    
    doc.setFont('helvetica', 'bold');
    doc.text('Running on:', detailsX, detailsStartY);
    doc.setFont('helvetica', 'normal');
    doc.text(maintenanceReport.security.runningOn, detailsX + 35, detailsStartY);
    detailsStartY += lineHeight;
    
    doc.setFont('helvetica', 'bold');
    doc.text('CMS:', detailsX, detailsStartY);
    doc.setFont('helvetica', 'normal');
    doc.text(maintenanceReport.security.cms, detailsX + 35, detailsStartY);
    detailsStartY += lineHeight;
    
    doc.setFont('helvetica', 'bold');
    doc.text('Powered by:', detailsX, detailsStartY);
    doc.setFont('helvetica', 'normal');
    doc.text(maintenanceReport.security.poweredBy || 'N/A', detailsX + 35, detailsStartY);
    
    yPosition += detailsCardHeight + lineHeight * 1.5; // Increased spacing after card section

    // Security Risk Meter
    checkNewPage(lineHeight * 4);
    yPosition += lineHeight * 1.0; // Add space before subsection title
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(15, 55, 50);
    doc.text('Security Risk Level', margin, yPosition);
    yPosition += lineHeight * 1.0; // Proper spacing

    const riskLevel = maintenanceReport.security.riskLevel || 'low';
    const riskScore = maintenanceReport.security.riskScore || 0;
    
    // Calculate risk meter position
    const getRiskPosition = (level) => {
      switch(level?.toLowerCase()) {
        case 'minimal': return 10;
        case 'low': return 30;
        case 'medium': return 50;
        case 'high': return 70;
        case 'critical': return 90;
        default: return 30;
      }
    };

    const getRiskFillWidth = (level) => {
      switch(level?.toLowerCase()) {
        case 'minimal': return 10;
        case 'low': return 30;
        case 'medium': return 50;
        case 'high': return 70;
        case 'critical': return 90;
        default: return 30;
      }
    };

    const pointerPosition = getRiskPosition(riskLevel);
    const fillWidth = getRiskFillWidth(riskLevel);
    const meterWidth = pageWidth - margin * 2;
    const meterHeight = 12;
    const meterY = yPosition;

    // Draw gradient bar background (light gray for unfilled portion)
    const segmentWidth = meterWidth / 5;
    const colors = [
      [22, 163, 74],   // Green - Minimal
      [132, 204, 22],  // Lime Green - Low
      [234, 179, 8],   // Yellow - Medium
      [249, 115, 22],  // Orange - High
      [220, 38, 38]    // Red - Critical
    ];
    
    // Draw light gray background for entire bar
    doc.setFillColor(243, 244, 246);
    doc.rect(margin, meterY, meterWidth, meterHeight, 'F');
    
    // Draw colored segments up to current risk level
    const fillEndX = margin + (meterWidth * fillWidth / 100);
    for (let i = 0; i < 5; i++) {
      const segStart = margin + i * segmentWidth;
      const segEnd = margin + (i + 1) * segmentWidth;
      if (fillEndX > segStart) {
        const fillSegEnd = Math.min(fillEndX, segEnd);
        doc.setFillColor(colors[i][0], colors[i][1], colors[i][2]);
        doc.rect(segStart, meterY, fillSegEnd - segStart, meterHeight, 'F');
      }
    }
    
    // Draw border
    doc.setDrawColor(209, 213, 219);
    doc.setLineWidth(0.5);
    doc.rect(margin, meterY, meterWidth, meterHeight, 'S');
    
    // Draw pointer (triangle-like shape using small rectangles)
    const pointerX = margin + (meterWidth * pointerPosition / 100);
    doc.setFillColor(31, 41, 55);
    doc.setDrawColor(31, 41, 55);
    const pointerSize = 4;
    const pointerTopY = meterY - pointerSize;
    
    // Draw triangle shape using small rectangles (simplified triangle)
    // Top point
    doc.rect(pointerX - 0.5, pointerTopY, 1, 1, 'F');
    // Middle section
    doc.rect(pointerX - 1.5, pointerTopY + 1, 3, 1, 'F');
    // Bottom section
    doc.rect(pointerX - 2, pointerTopY + 2, 4, 1, 'F');
    // Base
    doc.rect(pointerX - 2.5, meterY - 1, 5, 1, 'F');
    
    yPosition += meterHeight + lineHeight * 1.2;

    // Draw labels
    const labels = ['Minimal', 'Low Security Risk', 'Medium', 'High', 'Critical'];
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(107, 114, 128);
    
    for (let i = 0; i < 5; i++) {
      const labelX = margin + i * segmentWidth + segmentWidth / 2;
      const isActive = (i === 0 && riskLevel === 'minimal') ||
                      (i === 1 && riskLevel === 'low') ||
                      (i === 2 && riskLevel === 'medium') ||
                      (i === 3 && riskLevel === 'high') ||
                      (i === 4 && riskLevel === 'critical');
      
      if (isActive) {
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(15, 55, 50);
        doc.setFontSize(8);
      } else {
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(107, 114, 128);
        doc.setFontSize(7);
      }
      
      const labelText = labels[i];
      const textWidth = doc.getTextWidth(labelText);
      doc.text(labelText, labelX - textWidth / 2, yPosition);
    }
    
    yPosition += lineHeight * 0.8; // Reduced spacing

    // Current Risk text
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(75, 85, 99);
    const currentRiskText = `Current Risk: ${riskLevel.charAt(0).toUpperCase() + riskLevel.slice(1)}`;
    const currentRiskWidth = doc.getTextWidth(currentRiskText);
    doc.text(currentRiskText, (pageWidth - currentRiskWidth) / 2, yPosition);

    // Footer on each page and add logo to all pages except cover (page 1)
    const totalPages = doc.internal.getNumberOfPages();
    
    // Helper function to add logo to all pages except cover
    const addLogoToPages = async () => {
      try {
        // Load the logo image
        const img = new Image();
        img.crossOrigin = 'anonymous';
        
        await new Promise((resolve, reject) => {
          const timeout = setTimeout(() => {
            reject(new Error('Logo loading timeout'));
          }, 5000);
          
          img.onload = () => {
            clearTimeout(timeout);
            resolve();
          };
          img.onerror = (err) => {
            clearTimeout(timeout);
            console.error('Logo image error:', err);
            reject(err);
          };
          
          img.src = vajraLogo;
        });
        
        // Logo dimensions - small size for top right corner
        const logoHeight = 8; // mm - small size
        const logoAspectRatio = img.width / img.height;
        const logoWidth = logoHeight * logoAspectRatio;
        
        // Position at top right corner with small margin
        const logoMargin = 5; // mm from edges
        const logoX = pageWidth - logoWidth - logoMargin;
        const logoY = logoMargin;
        
        // Add logo to all pages except page 1 (cover page)
        for (let i = 2; i <= totalPages; i++) {
          doc.setPage(i);
          doc.addImage(vajraLogo, 'PNG', logoX, logoY, logoWidth, logoHeight);
        }
      } catch (error) {
        console.error('Error adding logo to PDF pages:', error);
        // Continue without logo if loading fails
      }
    };
    
    // Add logo to pages (except cover)
    await addLogoToPages();
    
    // Add footer to each page
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(107, 114, 128);
      doc.text(
        `Generated by Web Monitor - Page ${i} of ${totalPages}`,
        pageWidth / 2,
        pageHeight - 10,
        { align: 'center' }
      );
    }

    // Save PDF
    const siteNameForFile = (maintenanceReport.site.name || maintenanceReport.site.url).replace(/[^a-z0-9]/gi, '_').toLowerCase();
    const fileName = `maintenance-report-${siteNameForFile}-${maintenanceReport.month}-${maintenanceReport.year}.pdf`;
    doc.save(fileName);
  };

  // Helper function to add logo to all pages except cover (page 1)
  // skipCoverPage: if true, skip page 1 (for maintenance reports with cover page)
  //                if false, add logo to all pages including page 1 (for regular reports)
  const addLogoToAllPages = async (doc, pageWidth, pageHeight, skipCoverPage = false) => {
    try {
      // Load the logo image
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('Logo loading timeout'));
        }, 5000);
        
        img.onload = () => {
          clearTimeout(timeout);
          resolve();
        };
        img.onerror = (err) => {
          clearTimeout(timeout);
          console.error('Logo image error:', err);
          reject(err);
        };
        
        img.src = vajraLogo;
      });
      
      // Logo dimensions - small size for top right corner
      const logoHeight = 8; // mm - small size
      const logoAspectRatio = img.width / img.height;
      const logoWidth = logoHeight * logoAspectRatio;
      
      // Position at top right corner with small margin
      const logoMargin = 5; // mm from edges
      const logoX = pageWidth - logoWidth - logoMargin;
      const logoY = logoMargin;
      
      // Get total pages
      const totalPages = doc.internal.getNumberOfPages();
      
      // Add logo to pages - skip page 1 if skipCoverPage is true
      const startPage = skipCoverPage ? 2 : 1;
      for (let i = startPage; i <= totalPages; i++) {
        doc.setPage(i);
        doc.addImage(vajraLogo, 'PNG', logoX, logoY, logoWidth, logoHeight);
      }
    } catch (error) {
      console.error('Error adding logo to PDF pages:', error);
      // Continue without logo if loading fails
    }
  };

  const handleDownloadPdf = async () => {
    if (activeReport === 'maintenance-report') {
      handleDownloadMaintenanceReportPdf();
      return;
    }

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    const lineHeight = 7;
    let yPosition = margin;
    const maxY = pageHeight - margin;

    // Helper function to add a new page if needed
    const checkNewPage = (requiredSpace) => {
      if (yPosition + requiredSpace > maxY) {
        doc.addPage();
        yPosition = margin;
        return true;
      }
      return false;
    };

    // Get time range label
    const getTimeRangeLabel = () => {
      if (rangeType === 'last_30_days') return 'Last 30 days';
      if (rangeType === 'last_90_days') return 'Last 3 months';
      if (rangeType === 'custom_range') {
        if (fromDate && toDate) {
          return `${fromDate} to ${toDate}`;
        }
        return 'Custom range';
      }
      if (rangeType === 'single_day' && singleDate) {
        return singleDate;
      }
      return 'All time';
    };

    // Format date for PDF
    const formatDateForPdf = (date) => {
      if (!date) return 'N/A';
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      const hours = String(d.getHours()).padStart(2, '0');
      const minutes = String(d.getMinutes()).padStart(2, '0');
      const seconds = String(d.getSeconds()).padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    };

    // Title
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    
    if (activeReport === 'plugin-logs') {
      doc.text('Plugin Log Report', margin, yPosition);
    } else if (activeReport === 'backups') {
      doc.text('Backup & Restore Log Report', margin, yPosition);
    } else {
      doc.text('Security Activity Report', margin, yPosition);
    }
    
    yPosition += lineHeight * 2;

    // Report info
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(`Time Period: ${getTimeRangeLabel()}`, margin, yPosition);
    yPosition += lineHeight;
    doc.text(`Generated: ${formatDateForPdf(new Date())}`, margin, yPosition);
    yPosition += lineHeight * 2;

    if (activeReport === 'plugin-logs') {
      if (!filteredPluginLogs.length) {
        doc.setFontSize(10);
        doc.text('No plugin logs available for the selected period.', margin, yPosition);
        // Add logo to all pages (no cover page for this report)
        await addLogoToAllPages(doc, pageWidth, pageHeight, false);
        doc.save('webmonitor-plugin-logs.pdf');
        return;
      }

      // Table headers
      checkNewPage(lineHeight * 2);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      const headers = ['Timestamp', 'Website', 'Plugin', 'Status', 'Version', 'User', 'Message'];
      const colWidths = [32, 28, 22, 18, 22, 28, 32];
      let xPosition = margin;

      headers.forEach((header, index) => {
        doc.text(header, xPosition, yPosition);
        xPosition += colWidths[index];
      });
      yPosition += lineHeight * 1.5;

      // Draw header line
      doc.setLineWidth(0.5);
      doc.line(margin, yPosition - 3, pageWidth - margin, yPosition - 3);
      yPosition += lineHeight * 0.5;

      // Table rows
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      filteredPluginLogs.forEach((log) => {
        checkNewPage(lineHeight * 3);
        const created = log.createdAt ? new Date(log.createdAt) : null;
        const dateStr = created ? formatDateForPdf(created) : 'N/A';
        const versionText = log.oldVersion
          ? `${log.oldVersion}${log.newVersion ? ` → ${log.newVersion}` : ''}`
          : 'N/A';

        const rowData = [
          dateStr,
          (log.siteUrl || 'N/A').substring(0, 25),
          (log.pluginName || log.pluginSlug || 'Unknown').substring(0, 20),
          (log.action || '—').substring(0, 15),
          versionText.substring(0, 20),
          (log.email || 'N/A').substring(0, 25),
          (log.message || log.errorCode || '—').substring(0, 30), // Truncate long messages
        ];

        xPosition = margin;
        let maxLines = 1;
        rowData.forEach((cell, index) => {
          const cellText = String(cell);
          const maxWidth = colWidths[index] - 1;
          const lines = doc.splitTextToSize(cellText, maxWidth);
          if (lines.length > maxLines) maxLines = lines.length;
          doc.text(lines[0], xPosition, yPosition);
          xPosition += colWidths[index];
        });
        yPosition += lineHeight * (1 + (maxLines - 1) * 0.5);
      });

      // Add logo to all pages (no cover page for this report)
      await addLogoToAllPages(doc, pageWidth, pageHeight, false);
      doc.save('webmonitor-plugin-logs.pdf');
    } else if (activeReport === 'backups') {
      if (!backupLogs.length) {
        doc.setFontSize(10);
        doc.text('No backup logs available for the selected period.', margin, yPosition);
        // Add logo to all pages (no cover page for this report)
        await addLogoToAllPages(doc, pageWidth, pageHeight, false);
        doc.save('webmonitor-backup-logs.pdf');
        return;
      }

      // Table headers
      checkNewPage(lineHeight * 2);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      const headers = ['Date & Time', 'File Backup', 'Database Backup', 'Storage Type', 'Drive Status', 'User', 'Site'];
      const colWidths = [32, 22, 22, 22, 22, 32, 28];
      let xPosition = margin;

      headers.forEach((header, index) => {
        doc.text(header, xPosition, yPosition);
        xPosition += colWidths[index];
      });
      yPosition += lineHeight * 1.5;

      // Draw header line
      doc.setLineWidth(0.5);
      doc.line(margin, yPosition - 3, pageWidth - margin, yPosition - 3);
      yPosition += lineHeight * 0.5;

      // Table rows
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      backupLogs.forEach((log) => {
        checkNewPage(lineHeight * 3);
        const dateTime = log.dateTime ? new Date(log.dateTime) : null;
        const dateStr = dateTime
          ? `${dateTime.toLocaleDateString()} ${dateTime.toLocaleTimeString()}`
          : 'N/A';

        const driveStatusText = log.driveError
          ? `${log.driveStatus} (${log.driveError.substring(0, 12)})`
          : log.driveStatus;

        const rowData = [
          dateStr,
          log.fileBackupSize,
          log.databaseBackupSize,
          log.storageType === 'google-drive' ? 'Google Drive' : log.storageType === 'email' ? 'Email' : 'Local',
          driveStatusText,
          (log.userEmail || '').substring(0, 25),
          (log.siteName || 'Unknown Site').substring(0, 20), // Truncate long site names
        ];

        xPosition = margin;
        let maxLines = 1;
        rowData.forEach((cell, index) => {
          const cellText = String(cell);
          const maxWidth = colWidths[index] - 1;
          const lines = doc.splitTextToSize(cellText, maxWidth);
          if (lines.length > maxLines) maxLines = lines.length;
          doc.text(lines[0], xPosition, yPosition);
          xPosition += colWidths[index];
        });
        yPosition += lineHeight * (1 + (maxLines - 1) * 0.5);
      });

      // Add logo to all pages (no cover page for this report)
      await addLogoToAllPages(doc, pageWidth, pageHeight, false);
      doc.save('webmonitor-backup-logs.pdf');
    } else {
      // Security Activity - placeholder
      doc.setFontSize(10);
      doc.text('Security activity reports are available from the Security Monitoring page.', margin, yPosition);
      // Add logo to all pages (no cover page for this report)
      await addLogoToAllPages(doc, pageWidth, pageHeight, false);
      doc.save('webmonitor-security-report.pdf');
    }
  };

  // Generate Monthly Maintenance Report
  const generateMaintenanceReport = async () => {
    if (!selectedSiteId) {
      alert('Please select a site first');
      return;
    }

    if (!selectedMonth) {
      alert('Please select a month first');
      return;
    }

    setGeneratingReport(true);
    setReportError(null);
    setMaintenanceReport(null);

    try {
      const selectedSite = sites.find(s => s._id === selectedSiteId);
      if (!selectedSite) {
        throw new Error('Selected site not found');
      }

      // Get selected month info
      const selectedMonthData = availableMonths.find(m => m.value === selectedMonth);
      if (!selectedMonthData) {
        throw new Error('Selected month not found');
      }

      // Get selected month range (first day to last day of that month)
      const [year, month] = selectedMonth.split('-').map(Number);
      const firstDay = new Date(year, month - 1, 1);
      const lastDay = new Date(year, month, 0, 23, 59, 59, 999);

      // Fetch all required data in parallel
      const [monitorData, pluginsData, backupLogsData, pluginLogsData] = await Promise.all([
        axios.get(`/api/sites/${selectedSiteId}/monitor`).catch(() => ({ data: null })),
        axios.get(`/api/sites/${selectedSiteId}/plugins`).catch(() => ({ data: null })),
        axios.get(`/api/backup-request/logs?fromDate=${firstDay.toISOString()}&toDate=${lastDay.toISOString()}`).catch(() => ({ data: { logs: [] } })),
        axios.get(`/api/sites/logs/all?limit=1000`).catch(() => ({ data: { logs: [] } }))
      ]);

      // Extract data
      const monitor = monitorData.data?.data || monitorData.data || {};
      const transformedData = monitorData.data?.transformedData || {};
      const plugins = pluginsData.data?.plugins || [];
      const updatesList = pluginsData.data?.updatesList || [];
      const backups = backupLogsData.data?.logs || [];
      const allPluginLogs = pluginLogsData.data?.logs || pluginLogsData.data || [];

      // Filter backups for this site
      const siteBackups = backups.filter(b => {
        const backupSiteId = b.siteId?._id || b.siteId;
        const backupSiteUrl = b.siteUrl || b.siteId?.url;
        return backupSiteId === selectedSiteId || backupSiteUrl === selectedSite.url;
      });

      // Get WordPress and PHP versions
      const wpVersion = monitor.wp_version || transformedData.cms?.replace('WordPress ', '') || 'Unknown';
      const phpVersion = monitor.php_version || transformedData.powered_by?.replace('PHP ', '') || 'Unknown';

      // Get theme data
      const activeTheme = monitor.active_theme || {};
      const themeName = activeTheme.name || 'Custom theme';
      const themeVersion = activeTheme.version || 'N/A';

      // Helper function to compare versions
      const compareVersions = (current, latest) => {
        if (!current || !latest || current === 'Unknown' || latest === 'Unknown') return null;
        const currentParts = current.split('.').map(Number);
        const latestParts = latest.split('.').map(Number);
        for (let i = 0; i < Math.max(currentParts.length, latestParts.length); i++) {
          const curr = currentParts[i] || 0;
          const lat = latestParts[i] || 0;
          if (curr < lat) return -1; // Update available
          if (curr > lat) return 1;  // Current is newer
        }
        return 0; // Same version
      };

      // Get WordPress latest version from update data if available
      // WordPress stores this in _site_transient_update_core, but we can check monitor data
      const wpLatestVersion = monitor.update_core?.version || 
                              monitor.wp_latest_version || 
                              wpVersion; // Fallback to current if not available

      // Get PHP latest version (this would ideally come from an API, but for now we'll use a known latest)
      // PHP 8.4.1 is the latest as of 2026, but hosting providers may not support it
      const phpLatestVersion = '8.4.1';

      // Check for updates
      const wpUpdateStatus = compareVersions(wpVersion, wpLatestVersion);
      const phpUpdateStatus = compareVersions(phpVersion, phpLatestVersion);

      // Helper function to format date
      const formatUpdateDate = (dateString) => {
        if (!dateString) return '-';
        try {
          return new Date(dateString).toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          });
        } catch {
          return '-';
        }
      };

      // Get previous versions from plugin logs (if available)
      // Look for update_success logs in the selected month to find previous versions
      const pluginLogsForMonth = allPluginLogs.filter(log => {
        if (!log.createdAt) return false;
        const logSiteId = log.siteId?._id || log.siteId;
        if (logSiteId !== selectedSiteId) return false;
        const logDate = new Date(log.createdAt);
        return logDate >= firstDay && logDate <= lastDay;
      });

      // Find WordPress update logs
      const wpUpdateLogs = pluginLogsForMonth.filter(log => 
        log.pluginName?.toLowerCase().includes('wordpress') || 
        log.pluginSlug?.toLowerCase().includes('wordpress') ||
        log.message?.toLowerCase().includes('wordpress')
      );
      
      // Get previous WordPress version and update date from logs
      const wpUpdateSuccessLog = wpUpdateLogs
        .filter(log => log.action === 'update_success' && log.oldVersion)
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];
      
      const wpPreviousVersion = wpUpdateSuccessLog?.oldVersion || wpVersion;
      const wpUpdateDate = wpUpdateSuccessLog?.createdAt 
        ? formatUpdateDate(wpUpdateSuccessLog.createdAt)
        : '-';

      // Find PHP update logs (PHP updates might be tracked differently)
      // Look for logs mentioning PHP version changes
      const phpUpdateLogs = allPluginLogs.filter(log => {
        if (!log.createdAt) return false;
        const logSiteId = log.siteId?._id || log.siteId;
        if (logSiteId !== selectedSiteId) return false;
        const logDate = new Date(log.createdAt);
        // Check logs from before the selected month to find when PHP was last updated
        const beforeMonth = new Date(year, month - 2, 0); // Last day of month before selected
        return logDate <= beforeMonth && (
          log.message?.toLowerCase().includes('php') ||
          log.pluginName?.toLowerCase().includes('php')
        );
      });

      const phpUpdateSuccessLog = phpUpdateLogs
        .filter(log => log.action === 'update_success')
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];
      
      // Get previous PHP version from update logs if available
      const phpPreviousVersion = phpUpdateSuccessLog?.oldVersion || phpVersion;
      
      const phpUpdateDate = phpUpdateSuccessLog?.createdAt
        ? formatUpdateDate(phpUpdateSuccessLog.createdAt)
        : (phpPreviousVersion !== phpVersion ? '-' : '-'); // Only show date if version changed

      // Find theme update logs in the selected month
      const themeUpdateLogs = pluginLogsForMonth.filter(log => 
        (log.pluginName?.toLowerCase().includes('theme') ||
         log.message?.toLowerCase().includes('theme')) &&
        log.action === 'update_success'
      );

      const themeUpdateSuccessLog = themeUpdateLogs
        .filter(log => log.oldVersion && log.newVersion)
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];
      
      const themePreviousVersion = themeUpdateSuccessLog?.oldVersion || 
        (themeName === 'Custom theme' ? 'Custom theme' : themeVersion);
      const themeUpdateDate = themeUpdateSuccessLog?.createdAt
        ? formatUpdateDate(themeUpdateSuccessLog.createdAt)
        : (themePreviousVersion !== (themeName === 'Custom theme' ? 'Custom theme' : themeVersion) ? '-' : '-');

      // Helper function to extract plugin slug
      const extractPluginSlug = (plugin) => {
        // Try multiple sources for slug
        if (plugin.slug) {
          return plugin.slug.trim().toLowerCase();
        }
        if (plugin.plugin) {
          const parts = plugin.plugin.split('/');
          const slug = parts[0] || plugin.plugin.replace('.php', '');
          return slug.trim().toLowerCase();
        }
        if (plugin.plugin_file) {
          const parts = plugin.plugin_file.split('/');
          const slug = parts[0] || plugin.plugin_file.replace('.php', '');
          return slug.trim().toLowerCase();
        }
        return null;
      };

      // Helper function to fetch changelog from backend API (which proxies WordPress.org API)
      const fetchPluginChangelog = async (slug) => {
        if (!slug) return null;
        try {
          const normalizedSlug = slug.trim().toLowerCase();
          console.log(`[Reports] Fetching changelog for slug: ${normalizedSlug}`);
          
          // Use backend API endpoint to avoid CORS issues
          const response = await axios.get(`/api/plugin-changelog/${encodeURIComponent(normalizedSlug)}`, {
            timeout: 10000,
            headers: {
              'Accept': 'application/json'
            }
          });
          
          if (response.data?.success && response.data?.data) {
            console.log(`[Reports] Successfully fetched changelog for ${normalizedSlug}`);
            return response.data.data;
          } else {
            console.log(`[Reports] No changelog data returned for ${normalizedSlug}`);
            return null;
          }
        } catch (error) {
          console.warn(`[Reports] Failed to fetch changelog for ${slug}:`, error.message);
          if (error.response) {
            console.warn(`[Reports] Response status: ${error.response.status}`);
          }
        }
        return null;
      };

      // Helper function to format date as DD.MM.YYYY (as shown in the image)
      const formatUpdateDateShort = (dateString) => {
        if (!dateString) return null;
        try {
          const date = new Date(dateString);
          const day = String(date.getDate()).padStart(2, '0');
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const year = date.getFullYear();
          return `${day}.${month}.${year}`;
        } catch {
          return null;
        }
      };

      // Process plugin updates with changelog
      const pluginUpdatesWithChangelog = await Promise.all(
        plugins.map(async (plugin) => {
          const updateInfo = updatesList.find(u => 
            (u.plugin || u.plugin_file || u.slug) === (plugin.plugin || plugin.plugin_file || plugin.slug)
          );
          
          // Find update logs for this specific plugin
          // Try multiple matching strategies to find the correct plugin log
          const pluginSlug = extractPluginSlug(plugin);
          const pluginName = plugin.name || plugin.plugin || plugin.plugin_file || '';
          const pluginIdentifier = plugin.plugin || plugin.plugin_file || '';
          
          console.log(`[Reports] Processing plugin: ${pluginName}, slug: ${pluginSlug}, identifier: ${pluginIdentifier}`);
          
          // Find update logs for this plugin - check both in selected month and before
          // We need to check before the month to find the old version if update happened early in the month
          const allPluginLogsForSite = allPluginLogs.filter(log => {
            const logSiteId = log.siteId?._id || log.siteId;
            return logSiteId === selectedSiteId;
          });

          const pluginUpdateLogs = allPluginLogsForSite.filter(log => {
            const logPluginSlug = (log.pluginSlug || '').toLowerCase().trim();
            const logPluginName = (log.pluginName || '').toLowerCase().trim();
            const logPlugin = (log.plugin || log.plugin_file || '').toLowerCase().trim();
            
            // Match by slug (most reliable)
            if (pluginSlug && logPluginSlug) {
              if (logPluginSlug === pluginSlug) {
                console.log(`[Reports] Matched ${pluginName} by exact slug: ${logPluginSlug}`);
                return true;
              }
              // Also try matching slug parts
              if (logPluginSlug.includes(pluginSlug) || pluginSlug.includes(logPluginSlug)) {
                console.log(`[Reports] Matched ${pluginName} by partial slug: ${logPluginSlug} <-> ${pluginSlug}`);
                return true;
              }
            }
            
            // Match by plugin identifier (plugin/plugin_file)
            if (pluginIdentifier) {
              const normalizedPluginIdentifier = pluginIdentifier.toLowerCase().trim();
              if (normalizedPluginIdentifier && logPlugin) {
                if (logPlugin === normalizedPluginIdentifier) {
                  console.log(`[Reports] Matched ${pluginName} by exact identifier: ${logPlugin}`);
                  return true;
                }
                // Extract slug from plugin path (e.g., "elementor/elementor.php" -> "elementor")
                const logSlugFromPath = logPlugin.split('/')[0];
                const pluginSlugFromPath = normalizedPluginIdentifier.split('/')[0];
                if (logSlugFromPath && pluginSlugFromPath && logSlugFromPath === pluginSlugFromPath) {
                  console.log(`[Reports] Matched ${pluginName} by path slug: ${logSlugFromPath}`);
                  return true;
                }
              }
            }
            
            // Match by name (less reliable but fallback)
            if (pluginName && logPluginName) {
              const normalizedPluginName = pluginName.toLowerCase().trim();
              const normalizedLogName = logPluginName.toLowerCase().trim();
              if (normalizedLogName === normalizedPluginName) {
                console.log(`[Reports] Matched ${pluginName} by exact name: ${normalizedLogName}`);
                return true;
              }
              // Try partial match
              if (normalizedLogName.includes(normalizedPluginName) || normalizedPluginName.includes(normalizedLogName)) {
                // Additional check: make sure it's not too generic
                if (normalizedPluginName.length > 3 && normalizedLogName.length > 3) {
                  console.log(`[Reports] Matched ${pluginName} by partial name: ${normalizedLogName} <-> ${normalizedPluginName}`);
                  return true;
                }
              }
            }
            
            return false;
          });

          console.log(`[Reports] Found ${pluginUpdateLogs.length} total logs for ${pluginName}`);

          // Find the most recent update_success log for this plugin in the selected month
          const pluginUpdateSuccessLog = pluginUpdateLogs
            .filter(log => {
              const logDate = new Date(log.createdAt);
              // Check if log is in the selected month
              const inSelectedMonth = logDate >= firstDay && logDate <= lastDay;
              const isUpdateSuccess = log.action === 'update_success' && (log.oldVersion || log.newVersion);
              
              if (isUpdateSuccess) {
                console.log(`[Reports] Found update_success log for ${pluginName}: inMonth=${inSelectedMonth}, oldVersion=${log.oldVersion}, newVersion=${log.newVersion}, date=${log.createdAt}`);
              }
              
              return inSelectedMonth && isUpdateSuccess;
            })
            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];

          // Determine old and new versions
          let oldVersion = plugin.version || 'N/A';
          let newVersion = plugin.version || 'N/A';
          let updateDate = null;

          if (pluginUpdateSuccessLog) {
            // Plugin was updated in the selected month
            oldVersion = pluginUpdateSuccessLog.oldVersion || plugin.version || 'N/A';
            newVersion = pluginUpdateSuccessLog.newVersion || plugin.version || 'N/A';
            updateDate = formatUpdateDateShort(pluginUpdateSuccessLog.createdAt);
            
            console.log(`[Reports] Found update log for ${plugin.name}: ${oldVersion} -> ${newVersion} on ${updateDate}`);
          } else {
            // Plugin was not updated - both versions are the same (current version)
            oldVersion = plugin.version || 'N/A';
            newVersion = plugin.version || 'N/A';
            console.log(`[Reports] No update log found for ${plugin.name}, using current version: ${newVersion}`);
          }

          // Format newVersion with update date if available (format: version on first line, date in parentheses on second line)
          const newVersionDisplay = updateDate 
            ? `${newVersion}\n(${updateDate})`
            : newVersion;
          
          const slug = extractPluginSlug(plugin);
          let changelogData = null;
          let notesText = 'No new update';

          // Fetch changelog if plugin has a slug
          if (slug) {
            try {
              changelogData = await fetchPluginChangelog(slug);
              if (changelogData) {
                console.log(`[Reports] Successfully fetched changelog for ${plugin.name || slug}:`, changelogData);
              }
            } catch (err) {
              console.warn(`[Reports] Error fetching changelog for ${slug}:`, err.message);
            }
          } else {
            console.log(`[Reports] No slug extracted for plugin:`, plugin.name || plugin.plugin);
          }

          // Format notes based on changelog or update status
          if (changelogData && changelogData.changes && changelogData.changes.length > 0) {
            // Format: Version – Date\nAdded: ...\nChanged: ...
            const versionDate = changelogData.version || plugin.version || 'N/A';
            const changesText = changelogData.changes
              .map(change => {
                // Clean up the change text
                let cleaned = change.trim();
                // Remove HTML entities
                cleaned = cleaned.replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>');
                // Remove leading bullets, dashes, or asterisks
                cleaned = cleaned.replace(/^[•\-\*]\s*/, '').trim();
                
                // Ensure proper formatting - capitalize first letter
                if (cleaned.length > 0) {
                  cleaned = cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
                }
                
                return cleaned;
              })
              .filter(Boolean)
              .join('\n');
            
            notesText = `${versionDate}\n\n${changesText}`;
            console.log(`[Reports] Formatted notes for ${plugin.name}:`, notesText.substring(0, 150));
          } else if (updateInfo && updateInfo.new_version !== plugin.version) {
            notesText = 'Update available';
          } else {
            notesText = 'No new update';
          }

          return {
            name: plugin.name || plugin.plugin || 'Unknown',
            oldVersion: oldVersion,
            newVersion: newVersionDisplay,
            status: plugin.active ? 'Active' : 'Inactive',
            reason: 'N/A',
            notes: notesText
          };
        })
      );

      const pluginUpdates = pluginUpdatesWithChangelog;

      // Process backup data
      const totalBackups = siteBackups.length;
      const totalRestores = 0; // Restore functionality removed
      const avgBackupSize = siteBackups.length > 0 
        ? Math.round(siteBackups.reduce((sum, b) => {
            const fileSizeStr = b.fileBackupSize || b.sizeFiles || '0';
            const dbSizeStr = b.databaseBackupSize || b.sizeDb || '0';
            const fileSize = parseFloat(String(fileSizeStr).replace(/[^0-9.]/g, '')) || 0;
            const dbSize = parseFloat(String(dbSizeStr).replace(/[^0-9.]/g, '')) || 0;
            return sum + fileSize + dbSize;
          }, 0) / siteBackups.length)
        : 500; // Default to 500mb if no backups

      // Get security data
      const securityData = {
        malwareFound: transformedData.security_checks?.malware_detected === false || transformedData.security_checks?.malware_count === 0,
        blacklisted: transformedData.security_checks?.blacklisted === false,
        blacklistCount: transformedData.security_checks?.blacklist_count || 0,
        ip: transformedData.ip || transformedData.ip_address || monitor.ip || monitor.ip_address || '',
        hosting: transformedData.hosting || monitor.hosting || 'Unknown',
        runningOn: transformedData.runningOn || transformedData.server || monitor.runningOn || monitor.server || 'Unknown',
        cms: transformedData.cms || monitor.cms || 'WordPress',
        poweredBy: transformedData.powered_by || transformedData.poweredBy || monitor.powered_by || monitor.poweredBy || '',
        riskScore: transformedData.risk_score || 0,
        riskLevel: transformedData.risk_level || 'low'
      };

      // Build report
      const report = {
        site: selectedSite,
        month: selectedMonthData.month,
        year: selectedMonthData.year,
        monthValue: selectedMonth,
        generatedAt: new Date().toISOString(),
        coreUpdates: {
          php: {
            previousVersion: phpPreviousVersion,
            currentVersion: phpVersion,
            latestVersion: phpLatestVersion,
            notes: phpUpdateStatus === -1 ? 'Update available' : phpUpdateStatus === 0 ? 'Up to date' : '-',
            comments: phpUpdateStatus === -1 
              ? `PHP ${phpLatestVersion} is the latest version. Current version ${phpVersion} should be updated. Note: Some hosting providers (like WP Engine) may not release the latest PHP version immediately.`
              : phpUpdateStatus === 0
              ? `PHP ${phpVersion} is up to date.`
              : `PHP ${phpVersion} is current. Latest available is ${phpLatestVersion}, but some hosting providers may not support it yet.`,
            date: phpUpdateDate
          },
          wordpress: {
            previousVersion: wpPreviousVersion,
            currentVersion: wpVersion,
            latestVersion: wpLatestVersion,
            notes: wpUpdateStatus === -1 ? 'Update available' : wpUpdateStatus === 0 ? 'Up to date' : wpUpdateStatus === 1 ? 'Current version is newer' : 'Minor update',
            comments: wpUpdateStatus === -1
              ? `WordPress ${wpLatestVersion} is available. Current version ${wpVersion} should be updated for security and features.`
              : wpUpdateStatus === 0
              ? `WordPress ${wpVersion} is up to date.`
              : wpUpdateStatus === 1
              ? `Current version ${wpVersion} appears to be newer than reported latest ${wpLatestVersion}. This may be a development version.`
              : '-',
            date: wpUpdateDate
          },
          theme: {
            previousVersion: themePreviousVersion,
            currentVersion: themeName === 'Custom theme' ? 'Custom theme' : themeVersion,
            latestVersion: themeName === 'Custom theme' ? 'N/A' : themeVersion,
            notes: themeName === 'Custom theme' ? 'No updates required for custom theme' : '-',
            comments: themeName === 'Custom theme' 
              ? 'The custom theme is stable and no known security issues. Continue monitoring for vulnerabilities.'
              : '-',
            date: themeUpdateDate
          }
        },
        pluginUpdates: pluginUpdates,
        themeUpdates: {
          theme: themeName,
          oldVersion: themeName === 'Custom theme' ? 'N/A' : themeVersion,
          newVersion: themeName === 'Custom theme' ? 'N/A' : themeVersion,
          security: 'No vulnerabilities detected',
          status: themeName === 'Custom theme' ? 'N/A' : 'Active',
          recommendations: themeName === 'Custom theme' ? 'N/A' : '-'
        },
        backups: {
          total: totalBackups,
          restores: totalRestores,
          averageSize: `${avgBackupSize}mb`,
          frequency: 'Daily',
          weeklyBackups: siteBackups.filter(b => {
            const date = new Date(b.dateTime || b.createdAt);
            return date >= firstDay && date <= lastDay;
          }).map(b => new Date(b.dateTime || b.createdAt).getDate()),
          backupList: siteBackups
            .filter(b => {
              const date = new Date(b.dateTime || b.createdAt);
              return date >= firstDay && date <= lastDay;
            })
            .map(b => {
              const dateTime = new Date(b.dateTime || b.createdAt);
              const fileSizeStr = b.fileBackupSize || b.sizeFiles || '0';
              const dbSizeStr = b.databaseBackupSize || b.sizeDb || '0';
              return {
                dateTime: dateTime.toISOString(),
                date: dateTime.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
                time: dateTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
                fileBackupSize: fileSizeStr,
                databaseBackupSize: dbSizeStr,
                storageType: b.storageType || 'local',
                driveStatus: b.driveStatus || (b.storageType === 'google-drive' ? 'Success' : 'N/A'),
                driveError: b.driveError || null
              };
            })
            .sort((a, b) => new Date(b.dateTime) - new Date(a.dateTime)) // Sort by date, newest first
        },
        security: securityData
      };

      setMaintenanceReport(report);
    } catch (err) {
      console.error('[Reports] Failed to generate maintenance report:', err);
      setReportError(err.response?.data?.message || err.message || 'Failed to generate maintenance report.');
    } finally {
      setGeneratingReport(false);
    }
  };

  const renderPluginLogsSection = () => {
    if (logsLoading) {
      return <div className="reports-card">Loading global plugin logs…</div>;
    }

    if (logsError) {
      return <div className="reports-card error">Failed to load plugin logs: {logsError}</div>;
    }

    if (!pluginLogs.length) {
      return (
        <div className="reports-card">
          <p>No plugin logs have been recorded yet.</p>
          <p className="muted">
            As you activate, deactivate, and update plugins from WebMonitor, logs will appear here and
            can be exported as CSV or PDF.
          </p>
        </div>
      );
    }

    // Format date for PDF (YYYY-MM-DD HH:MM:SS)
    const formatDateForPdf = (date) => {
      if (!date) return 'N/A';
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      const hours = String(d.getHours()).padStart(2, '0');
      const minutes = String(d.getMinutes()).padStart(2, '0');
      const seconds = String(d.getSeconds()).padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    };

    const isUpToDateNoopLog = (log) => {
      const messageLower = (log?.message || '').toString().toLowerCase();
      const act = (log?.action || '').toString().toLowerCase();
      return (
        act === 'update_noop' ||
        messageLower.includes('already up to date') ||
        messageLower.includes('already up-to-date')
      );
    };

    // Get status color class based on action + message (fixes "already up to date" being logged as update_failed)
    const getStatusClass = (action, log) => {
      const act = (action || '').toLowerCase();
      if (isUpToDateNoopLog(log)) return 'status-success';
      if (act.includes('success') || act === 'activated') return 'status-success';
      if (act.includes('failed') || act.includes('error')) return 'status-critical';
      if (act === 'deactivated') return 'status-warning';
      return 'status-info';
    };

    // Get status text (fixes "already up to date" being shown as Failed)
    const getStatusText = (action, log) => {
      const act = (action || '').toLowerCase();
      if (isUpToDateNoopLog(log)) return 'Success';
      if (act.includes('success')) return 'Success';
      if (act.includes('failed') || act.includes('error')) return 'Failed';
      if (act === 'activated') return 'Activated';
      if (act === 'deactivated') return 'Deactivated';
      if (act === 'enabled_auto_update' || act.includes('enabled_auto_update')) return 'Enabled Auto Update';
      if (act === 'disabled_auto_update' || act.includes('disabled_auto_update')) return 'Disabled Auto Update';
      // Check for update_available first (update is available but not yet applied)
      if (act === 'update_available' || act.includes('update_available')) return 'Update';
      if (act === 'update_noop' || act.includes('update_noop')) return 'Success';
      // Check for update_success (update has been successfully applied)
      if (act === 'update_success' || act.includes('update_success')) return 'Updated';
      // For other update-related actions, default to 'Updated'
      if (act.includes('update')) return 'Updated';
      return action ? action.charAt(0).toUpperCase() + action.slice(1) : '—';
    };

    // Get time range label for PDF title
    const getTimeRangeLabel = () => {
      if (rangeType === 'last_30_days') return 'Last 30 days';
      if (rangeType === 'last_90_days') return 'Last 3 months';
      if (rangeType === 'custom_range') {
        if (fromDate && toDate) {
          return `${fromDate} to ${toDate}`;
        }
        return 'Custom range';
      }
      if (rangeType === 'single_day' && singleDate) {
        return singleDate;
      }
      return 'All time';
    };

    return (
      <div className="reports-card plugin-logs-card">
        {/* Print-only title */}
        <div className="print-only-header">
          <h1 className="print-title">Plugin Log Report</h1>
          <p className="print-subtitle">{getTimeRangeLabel()}</p>
          <p className="print-date">Generated: {formatDateForPdf(new Date())}</p>
        </div>

        {/* Screen-only header */}
        <div className="card-header screen-only">
          <h3>Plugin Logs (All Sites)</h3>
          <span>
            Showing {filteredPluginLogs.length.toLocaleString()} of{' '}
            {pluginLogs.length.toLocaleString()} events in the selected period
          </span>
        </div>

        {/* Screen-only metrics */}
        <div className="metrics-grid compact screen-only">
          <div className="metric-card small">
            <p>Total Events</p>
            <h2>{pluginSummary.total}</h2>
          </div>
          <div className="metric-card small">
            <p>Updates</p>
            <h2>{pluginSummary.updates}</h2>
          </div>
          <div className="metric-card small">
            <p>Activations</p>
            <h2>{pluginSummary.activations}</h2>
          </div>
          <div className="metric-card small">
            <p>Deactivations</p>
            <h2>{pluginSummary.deactivations}</h2>
          </div>
        </div>

        <div className="reports-table-wrapper">
          <table className="reports-table">
            <thead>
              <tr>
                <th>Timestamp</th>
                <th>Website</th>
                <th>Plugin</th>
                <th>Status</th>
                <th>Version</th>
                <th>User</th>
                <th>Message</th>
              </tr>
            </thead>
            <tbody>
              {filteredPluginLogs.map((log) => {
                const created = log.createdAt ? new Date(log.createdAt) : null;
                const dateStr = created ? formatDateForPdf(created) : 'N/A';
                const statusClass = getStatusClass(log.action, log);
                const statusText = getStatusText(log.action, log);
                const versionText = log.oldVersion
                  ? `${log.oldVersion}${log.newVersion ? ` → ${log.newVersion}` : ''}`
                  : 'N/A';
                return (
                  <tr key={log._id} className={`log-row ${statusClass}`}>
                    <td>{dateStr}</td>
                    <td>{log.siteUrl || 'N/A'}</td>
                    <td>{log.pluginName || log.pluginSlug || 'Unknown plugin'}</td>
                    <td className={`status-cell ${statusClass}`}>{statusText}</td>
                    <td>{versionText}</td>
                    <td>{log.email || 'N/A'}</td>
                    <td className="reports-message-cell">
                      {log.message || log.errorCode || '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Print-only footer */}
        <div className="print-only-footer">
          <p>Generated by Web Monitor</p>
        </div>
      </div>
    );
  };

  return (
    <div className="dashboard-container reports-page">
      <Sidebar />
      <div className="main-content">
        <div className="reports-header">
          <div>
            <p className="eyebrow">Insights / Reports</p>
            <h1>Reporting &amp; Exports</h1>
            <p className="subheading">
              Monitor every report generated by WebMonitor from a single place. Filter by time period and
              download logs as CSV or PDF for audits and stakeholders.
            </p>
          </div>
          <div className="actions">
            <Link to="/dashboard" className="ghost-link back-link">
              ← Back to dashboard
            </Link>
            <button type="button" onClick={handleDownloadPdf} className="export-button">
              Download PDF
            </button>
          </div>
        </div>

        <div className="reports-grid overview-grid">
          <div className="reports-card">
            <div className="card-header">
              <h3>Report Types</h3>
              <span>Everything WebMonitor tracks, in one hub</span>
            </div>
            <div className="report-tabs">
              <button
                type="button"
                className={activeReport === 'plugin-logs' ? 'tab active' : 'tab'}
                onClick={() => setActiveReport('plugin-logs')}
              >
                Plugin Logs
              </button>
              <button
                type="button"
                className={activeReport === 'backups' ? 'tab active' : 'tab'}
                onClick={() => setActiveReport('backups')}
              >
                Backup Downloads
              </button>
              <button
                type="button"
                className={activeReport === 'maintenance-report' ? 'tab active' : 'tab'}
                onClick={() => setActiveReport('maintenance-report')}
              >
                Monthly Maintenance Report
              </button>
            </div>
            <p className="muted">
              Use the tabs to choose which report you want to monitor. All exports respect the date filters
              you set on this page.
            </p>
          </div>

          <div className="reports-card">
            <div className="card-header">
              <h3>Time Window</h3>
              <span>Quick presets and fully custom ranges</span>
            </div>
            <div className="time-filters">
              <div className="time-range-select">
                <label htmlFor="timeRangeSelect">Time Range</label>
                <select
                  id="timeRangeSelect"
                  value={rangeType}
                  onChange={(e) => setRangeType(e.target.value)}
                  className="time-range-dropdown"
                >
                  <option value="last_30_days">Last 30 days</option>
                  <option value="last_90_days">Last 3 months</option>
                  <option value="custom_range">Custom range</option>
                  <option value="single_day">Specific day</option>
                </select>
              </div>

              {rangeType === 'custom_range' && (
                <div className="date-row">
                  <div className="date-field">
                    <label htmlFor="fromDate">From</label>
                    <input
                      id="fromDate"
                      type="date"
                      value={fromDate}
                      onChange={(e) => setFromDate(e.target.value)}
                    />
                  </div>
                  <div className="date-field">
                    <label htmlFor="toDate">To</label>
                    <input
                      id="toDate"
                      type="date"
                      value={toDate}
                      onChange={(e) => setToDate(e.target.value)}
                    />
                  </div>
                </div>
              )}

              {rangeType === 'single_day' && (
                <div className="date-row">
                  <div className="date-field">
                    <label htmlFor="singleDate">Day</label>
                    <input
                      id="singleDate"
                      type="date"
                      value={singleDate}
                      onChange={(e) => setSingleDate(e.target.value)}
                    />
                  </div>
                </div>
              )}

              <div className="export-actions-row">
                <button type="button" className="secondary-button" onClick={handleExportCsv}>
                  Export current view (CSV)
                </button>
                <button type="button" className="secondary-button" onClick={handleDownloadPdf}>
                  Export current view (PDF)
                </button>
              </div>
            </div>
          </div>
        </div>

        {activeReport === 'plugin-logs' && renderPluginLogsSection()}

        {activeReport === 'backups' && (
          <div className="reports-card backup-logs-card">
            {/* Print-only title */}
            <div className="print-only-header">
              <h1 className="print-title">Backup &amp; Restore Log Report</h1>
              <p className="print-subtitle">
                {rangeType === 'last_30_days'
                  ? 'Last 30 days'
                  : rangeType === 'last_90_days'
                  ? 'Last 3 months'
                  : rangeType === 'custom_range' && fromDate && toDate
                  ? `${fromDate} to ${toDate}`
                  : rangeType === 'single_day' && singleDate
                  ? singleDate
                  : 'All time'}
              </p>
              <p className="print-date">Generated: {new Date().toLocaleString()}</p>
            </div>

            {/* Screen-only header */}
            <div className="card-header screen-only">
              <h3>Backup &amp; Restore Reports</h3>
              <span>Download backup archives and review backup history</span>
            </div>

            {backupLogsLoading ? (
              <div className="reports-loading">Loading backup logs…</div>
            ) : backupLogsError ? (
              <div className="reports-error">Failed to load backup logs: {backupLogsError}</div>
            ) : backupLogs.length === 0 ? (
              <div>
                <p>No backup logs found for the selected time period.</p>
                <p className="muted">
                  Backup jobs, restore operations, and downloadable archives are managed from the{' '}
                  <strong>Backup &amp; Restore</strong> area.
                </p>
                <Link to="/backup" className="primary-link-button">
                  Go to Backup &amp; Restore
                </Link>
              </div>
            ) : (
              <>
                <div className="screen-only">
                  <p>
                    Showing {backupLogs.length} backup{backupLogs.length !== 1 ? 's' : ''} in the selected
                    period. Backup jobs, restore operations, and downloadable archives are managed from the{' '}
                    <strong>Backup &amp; Restore</strong> area.
                  </p>
                </div>

                <div className="reports-table-wrapper">
                  <table className="reports-table">
                    <thead>
                      <tr>
                        <th>Date &amp; Time</th>
                        <th>File Backup</th>
                        <th>Database Backup</th>
                        <th>Storage Type</th>
                        <th>Drive Status</th>
                        <th>User</th>
                        <th>Site</th>
                      </tr>
                    </thead>
                    <tbody>
                      {backupLogs.map((log) => {
                        const dateTime = log.dateTime ? new Date(log.dateTime) : null;
                        const dateStr = dateTime
                          ? `${dateTime.toLocaleDateString()} ${dateTime.toLocaleTimeString()}`
                          : 'N/A';

                        const statusClass =
                          log.driveStatus === 'Success'
                            ? 'status-success'
                            : log.driveStatus === 'Failed'
                            ? 'status-critical'
                            : 'status-warning';

                        return (
                          <tr key={log._id} className={`log-row ${statusClass}`}>
                            <td>{dateStr}</td>
                            <td>{log.fileBackupSize}</td>
                            <td>{log.databaseBackupSize}</td>
                            <td>
                              {log.storageType === 'google-drive'
                                ? 'Google Drive'
                                : log.storageType === 'email'
                                ? 'Email'
                                : 'Local'}
                            </td>
                            <td className={`status-cell ${statusClass}`}>
                              {log.driveStatus}
                              {log.driveError && (
                                <span className="error-detail" title={log.driveError}>
                                  {' '}
                                  ({log.driveError})
                                </span>
                              )}
                            </td>
                            <td>{log.userEmail}</td>
                            <td>
                              {log.siteName}
                              {log.siteUrl && (
                                <span className="muted" style={{ display: 'block', fontSize: '0.85em' }}>
                                  {log.siteUrl}
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Print-only footer */}
                <div className="print-only-footer">
                  <p>Generated by Web Monitor</p>
                </div>
              </>
            )}
          </div>
        )}

        {activeReport === 'maintenance-report' && (
          <div className="reports-card maintenance-report-card">
            {/* Print-only title */}
            <div className="print-only-header">
              <h1 className="print-title">Monthly Maintenance Report</h1>
              <p className="print-subtitle">
                {maintenanceReport ? `${maintenanceReport.site.name || maintenanceReport.site.url} - ${maintenanceReport.month} ${maintenanceReport.year}` : 'Select a site to generate report'}
              </p>
              <p className="print-date">Generated: {maintenanceReport ? new Date(maintenanceReport.generatedAt).toLocaleString() : new Date().toLocaleString()}</p>
            </div>

            {/* Screen-only header */}
            <div className="card-header screen-only">
              <h3>Monthly Maintenance Report</h3>
              <span>Generate comprehensive maintenance reports for each site</span>
            </div>

            {/* Site Selection */}
            <div className="maintenance-report-controls screen-only">
              <div className="site-select-wrapper">
                <label htmlFor="siteSelect">Select Site</label>
                <select
                  id="siteSelect"
                  value={selectedSiteId}
                  onChange={(e) => setSelectedSiteId(e.target.value)}
                  className="site-select-dropdown"
                >
                  <option value="">-- Select a site --</option>
                  {sites.map(site => (
                    <option key={site._id} value={site._id}>
                      {site.name || site.url}
                    </option>
                  ))}
                </select>
              </div>
              <div className="month-select-wrapper">
                <label htmlFor="monthSelect">Select Month</label>
                <select
                  id="monthSelect"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  className="site-select-dropdown"
                >
                  <option value="">-- Select a month --</option>
                  {availableMonths.map(month => (
                    <option key={month.value} value={month.value}>
                      {month.label}
                    </option>
                  ))}
                </select>
              </div>
              <button
                type="button"
                onClick={generateMaintenanceReport}
                disabled={!selectedSiteId || !selectedMonth || generatingReport}
                className="generate-report-button"
              >
                {generatingReport ? 'Generating...' : 'Generate Monthly Maintenance Report'}
              </button>
              {maintenanceReport && (
                <button
                  type="button"
                  onClick={handleDownloadMaintenanceReportPdf}
                  className="download-pdf-button"
                >
                  📥 Download PDF
                </button>
              )}
            </div>

            {reportError && (
              <div className="reports-error">Error: {reportError}</div>
            )}

            {maintenanceReport && (
              <div className="maintenance-report-content">
                {/* Cover Page */}
                <div className="maintenance-cover-page">
                  <div className="cover-page-content">
                    <div className="cover-logo-section">
                      <div className="cover-logo">
                        <img src={logoImage} alt="Vajra Global Logo" />
                      </div>
                    </div>
                    <div className="cover-title-section">
                      <h1 className="cover-main-title">
                        <span className="cover-title-line1">Website Maintenance /</span>
                        <span className="cover-title-line2">{maintenanceReport.site.name || maintenanceReport.site.url}</span>
                      </h1>
                      <div className="cover-title-line"></div>
                      <p className="cover-subtitle">Monthly Report - {maintenanceReport.month} - {maintenanceReport.year}</p>
                    </div>
                    <div className="cover-footer">
                      <div className="cover-generated-date">
                        Generated: {new Date(maintenanceReport.generatedAt).toLocaleDateString('en-CA')}
                      </div>
                      <div className="cover-page-info">
                        Generated by Web Monitor - Page 1 of 1
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Overview Section */}
                <div className="maintenance-section">
                  <div className="section-header">
                    <span className="section-icon">📋</span>
                    <h4>Overview</h4>
                  </div>
                  <table className="maintenance-table">
                    <thead>
                      <tr>
                        <th>Work Plan / Task List</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>
                          <span className="task-icon">⚙️</span>
                          Core update
                        </td>
                        <td className={maintenanceReport.coreUpdates && (maintenanceReport.coreUpdates.wordpress || maintenanceReport.coreUpdates.php) ? "status-success" : "status-warning"}>
                          {maintenanceReport.coreUpdates && (maintenanceReport.coreUpdates.wordpress || maintenanceReport.coreUpdates.php) ? '✓' : 'N/A'}
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <span className="task-icon">⚡</span>
                          Plugin Update (Staging & Live)
                        </td>
                        <td className={maintenanceReport.pluginUpdates && maintenanceReport.pluginUpdates.length > 0 ? "status-success" : "status-warning"}>
                          {maintenanceReport.pluginUpdates && maintenanceReport.pluginUpdates.length > 0 ? '✓' : 'N/A'}
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <span className="task-icon">🎨</span>
                          Theme Update
                        </td>
                        <td className={maintenanceReport.themeUpdates && maintenanceReport.themeUpdates.theme ? "status-success" : "status-warning"}>
                          {maintenanceReport.themeUpdates && maintenanceReport.themeUpdates.theme ? '✓' : 'N/A'}
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <span className="task-icon">💾</span>
                          Backups (Weekly & Daily Backups)
                        </td>
                        <td className={(maintenanceReport.backups && ((maintenanceReport.backups.total > 0) || (maintenanceReport.backups.backupList && maintenanceReport.backups.backupList.length > 0))) ? "status-success" : "status-warning"}>
                          {(maintenanceReport.backups && ((maintenanceReport.backups.total > 0) || (maintenanceReport.backups.backupList && maintenanceReport.backups.backupList.length > 0))) ? '✓' : 'N/A'}
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <span className="task-icon">🔒</span>
                          Security Monitoring
                        </td>
                        <td className={(maintenanceReport.security && (maintenanceReport.security.malwareFound !== undefined || maintenanceReport.security.blacklisted !== undefined || maintenanceReport.security.riskLevel)) ? "status-success" : "status-warning"}>
                          {(maintenanceReport.security && (maintenanceReport.security.malwareFound !== undefined || maintenanceReport.security.blacklisted !== undefined || maintenanceReport.security.riskLevel)) ? '✓' : 'N/A'}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Core Updates Section */}
                <div className="maintenance-section">
                  <div className="section-header">
                    <span className="section-icon">⚙️</span>
                    <h4>Core Updates</h4>
                  </div>
                  <table className="maintenance-table">
                    <thead>
                      <tr>
                        <th>Category</th>
                        <th>Previous Version</th>
                        <th>Current Version</th>
                        <th>Latest Version</th>
                        <th>Notes</th>
                        <th>Comments</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>PHP Updates</td>
                        <td>{maintenanceReport.coreUpdates.php.previousVersion}</td>
                        <td>{maintenanceReport.coreUpdates.php.currentVersion}</td>
                        <td>{maintenanceReport.coreUpdates.php.latestVersion}</td>
                        <td>{maintenanceReport.coreUpdates.php.notes}</td>
                        <td>{maintenanceReport.coreUpdates.php.comments}</td>
                        <td>{maintenanceReport.coreUpdates.php.date}</td>
                      </tr>
                      <tr>
                        <td>WordPress</td>
                        <td>{maintenanceReport.coreUpdates.wordpress.previousVersion}</td>
                        <td>{maintenanceReport.coreUpdates.wordpress.currentVersion}</td>
                        <td>{maintenanceReport.coreUpdates.wordpress.latestVersion}</td>
                        <td>{maintenanceReport.coreUpdates.wordpress.notes}</td>
                        <td>{maintenanceReport.coreUpdates.wordpress.comments}</td>
                        <td>{maintenanceReport.coreUpdates.wordpress.date}</td>
                      </tr>
                      <tr>
                        <td>Theme Updates</td>
                        <td>{maintenanceReport.coreUpdates.theme.previousVersion}</td>
                        <td>{maintenanceReport.coreUpdates.theme.currentVersion}</td>
                        <td>{maintenanceReport.coreUpdates.theme.latestVersion}</td>
                        <td>{maintenanceReport.coreUpdates.theme.notes}</td>
                        <td>{maintenanceReport.coreUpdates.theme.comments}</td>
                        <td>{maintenanceReport.coreUpdates.theme.date}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Plugin Updates Section */}
                <div className="maintenance-section">
                  <div className="section-header">
                    <span className="section-icon">⚡</span>
                    <h4>Plugin Updates</h4>
                  </div>
                  <table className="maintenance-table">
                    <thead>
                      <tr>
                        <th>Plugin Name</th>
                        <th>Old Version</th>
                        <th>New Version (Updated on)</th>
                        <th>Status</th>
                        <th>Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {maintenanceReport.pluginUpdates.map((plugin, idx) => (
                        <tr key={idx}>
                          <td>{plugin.name}</td>
                          <td>{plugin.oldVersion}</td>
                          <td style={{ whiteSpace: 'pre-line' }}>{plugin.newVersion}</td>
                          <td className={plugin.status === 'Active' ? 'status-success' : 'status-warning'}>{plugin.status}</td>
                          <td className="plugin-notes-cell" style={{ whiteSpace: 'pre-line', fontSize: '0.85rem' }}>{plugin.notes}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Theme Updates Section */}
                <div className="maintenance-section">
                  <div className="section-header">
                    <span className="section-icon">🎨</span>
                    <h4>Theme Updates</h4>
                  </div>
                  <table className="maintenance-table">
                    <thead>
                      <tr>
                        <th>Theme</th>
                        <th>Old Version</th>
                        <th>New Version (Updated on)</th>
                        <th>Security</th>
                        <th>Status</th>
                        <th>Recommendations</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>{maintenanceReport.themeUpdates.theme}</td>
                        <td>{maintenanceReport.themeUpdates.oldVersion}</td>
                        <td>{maintenanceReport.themeUpdates.newVersion}</td>
                        <td>{maintenanceReport.themeUpdates.security}</td>
                        <td>{maintenanceReport.themeUpdates.status}</td>
                        <td>{maintenanceReport.themeUpdates.recommendations}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Backup Section */}
                <div className="maintenance-section">
                  <div className="section-header">
                    <span className="section-icon">💾</span>
                    <h4>Backup - Daily/Weekly</h4>
                  </div>
                  <table className="maintenance-table">
                    <thead>
                      <tr>
                        <th>Summary</th>
                        <th>Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Total Backups</td>
                        <td>{maintenanceReport.backups.total}</td>
                      </tr>
                      <tr>
                        <td>Total Restores</td>
                        <td>0</td>
                      </tr>
                      <tr>
                        <td>Average Backup Size</td>
                        <td>{maintenanceReport.backups.averageSize}</td>
                      </tr>
                      <tr>
                        <td>Backup Frequency</td>
                        <td>{maintenanceReport.backups.frequency}</td>
                      </tr>
                    </tbody>
                  </table>
                  
                  {/* Individual Backup Details */}
                  {maintenanceReport.backups.backupList && maintenanceReport.backups.backupList.length > 0 && (
                    <div style={{ marginTop: '24px' }}>
                      <h5 style={{ marginBottom: '12px', fontSize: '14px', fontWeight: '600', color: '#0f3732' }}>
                        Backup Details
                      </h5>
                      <table className="maintenance-table">
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Time</th>
                            <th>File Backup Size</th>
                            <th>Database Backup Size</th>
                            <th>Storage Type</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {maintenanceReport.backups.backupList.map((backup, idx) => (
                            <tr key={idx}>
                              <td>{backup.date}</td>
                              <td>{backup.time}</td>
                              <td>{backup.fileBackupSize || 'N/A'}</td>
                              <td>{backup.databaseBackupSize || 'N/A'}</td>
                              <td>
                                {backup.storageType === 'google-drive'
                                  ? 'Google Drive'
                                  : backup.storageType === 'email'
                                  ? 'Email'
                                  : 'Local'}
                              </td>
                              <td className={backup.driveStatus === 'Success' ? 'status-success' : backup.driveStatus === 'Failed' ? 'status-critical' : 'status-warning'}>
                                {backup.driveStatus}
                                {backup.driveError && (
                                  <span className="error-detail" title={backup.driveError} style={{ display: 'block', fontSize: '0.85em', color: '#dc2626', marginTop: '4px' }}>
                                    {backup.driveError}
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* Security Monitoring Section */}
                <div className="maintenance-section">
                  <div className="section-header">
                    <span className="section-icon">🔒</span>
                    <h4>Security Monitoring</h4>
                  </div>
                  <div className="security-monitoring-content">
                    <div className="security-status-cards">
                      <div className="security-status-card success">
                        <span className="status-icon">✓</span>
                        <div>
                          <strong>No Malware Found</strong>
                          <p>Our scanner didn't detect any malware</p>
                        </div>
                      </div>
                      <div className="security-status-card success">
                        <span className="status-icon">✓</span>
                        <div>
                          <strong>Site is not Blacklisted</strong>
                          <p>{maintenanceReport.security.blacklistCount} Blacklists checked</p>
                        </div>
                      </div>
                    </div>
                    <div className="website-details-card">
                      <div className="website-details-wrapper">
                        <div className="website-info-left">
                          <span className="website-icon">📄</span>
                          <span className="website-url">{maintenanceReport.site.url}</span>
                        </div>
                        <div className="website-details-right">
                          <p><strong>IP address:</strong> {maintenanceReport.security.ip || 'N/A'}</p>
                          <p><strong>Hosting:</strong> {maintenanceReport.security.hosting}</p>
                          <p><strong>Running on:</strong> {maintenanceReport.security.runningOn}</p>
                          <p><strong>CMS:</strong> {maintenanceReport.security.cms}</p>
                          <p><strong>Powered by:</strong> {maintenanceReport.security.poweredBy || 'N/A'}</p>
                        </div>
                      </div>
                    </div>
                    <div className="security-risk-meter">
                      {(() => {
                        // Calculate position based on risk level
                        const getRiskPosition = (riskLevel) => {
                          switch(riskLevel?.toLowerCase()) {
                            case 'minimal': return 10; // 10% position
                            case 'low': return 30; // 30% position (Low Security Risk)
                            case 'medium': return 50; // 50% position
                            case 'high': return 70; // 70% position
                            case 'critical': return 90; // 90% position
                            default: return 30; // Default to Low
                          }
                        };

                        const getRiskFillWidth = (riskLevel) => {
                          switch(riskLevel?.toLowerCase()) {
                            case 'minimal': return 10;
                            case 'low': return 30;
                            case 'medium': return 50;
                            case 'high': return 70;
                            case 'critical': return 90;
                            default: return 30;
                          }
                        };

                        const riskLevel = maintenanceReport.security.riskLevel || 'low';
                        const pointerPosition = getRiskPosition(riskLevel);
                        const fillWidth = getRiskFillWidth(riskLevel);

                        const getRiskColor = (level) => {
                          switch(level?.toLowerCase()) {
                            case 'minimal': return '#16a34a';
                            case 'low': return '#eab308';
                            case 'medium': return '#f97316';
                            case 'high': return '#dc2626';
                            case 'critical': return '#991b1b';
                            default: return '#eab308';
                          }
                        };

                        const getRiskLabel = (level) => {
                          switch(level?.toLowerCase()) {
                            case 'minimal': return 'Minimal';
                            case 'low': return 'Low';
                            case 'medium': return 'Medium';
                            case 'high': return 'High';
                            case 'critical': return 'Critical';
                            default: return 'Low';
                          }
                        };

                        return (
                          <>
                            <div className="risk-meter-bar">
                              <div 
                                className="risk-meter-fill" 
                                style={{ 
                                  width: `${fillWidth}%`
                                }}
                              ></div>
                              <div 
                                className="risk-meter-pointer" 
                                style={{ 
                                  left: `${pointerPosition}%`
                                }}
                              ></div>
                            </div>
                            <div className="risk-meter-labels">
                              <span className={riskLevel === 'minimal' ? 'active' : ''}>Minimal</span>
                              <span className={riskLevel === 'low' ? 'active' : ''}>Low Security Risk</span>
                              <span className={riskLevel === 'medium' ? 'active' : ''}>Medium</span>
                              <span className={riskLevel === 'high' ? 'active' : ''}>High</span>
                              <span className={riskLevel === 'critical' ? 'active' : ''}>Critical</span>
                            </div>
                            <div className="current-risk-text">
                              Current Risk: <span className="current-risk-value" style={{ color: getRiskColor(riskLevel) }}>{getRiskLabel(riskLevel)}</span>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Print-only footer */}
            <div className="print-only-footer">
              <p>Generated by Web Monitor</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Reports;

